<template>
  <div class="dashboard-layout">
    <!-- Sidebar removed as requested -->

    <main class="dashboard-main no-sidebar">
      <div class="page-header-wrapper">
        <Header :showLogo="false" :backRoute="'/manage-appointments'" />
      </div>

      <div class="dashboard-container">
        <!-- Dashboard Content Header -->
        <div class="dashboard-content-header">
          <div class="dashboard-title-row">
            <router-link to="/manage-appointments" class="btn-operations-dashboard">
              <i class="fa-solid fa-arrow-left"></i>
              <span>Back to Operations</span>
            </router-link>
          </div>
          <div class="dashboard-filters-row">
            <div class="title-section">
              <h4>Analytics Dashboard</h4>
            </div>
            <div class="header-actions">
              <div class="timeframe-dropdown">
                <select v-model="selectedTimeframe" @change="handleTimeframeChange">
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="custom">Custom Range</option>
                </select>
                <i class="fa-solid fa-chevron-down dropdown-icon"></i>
              </div>
              <div class="timeframe-dropdown">
                <select v-model="selectedEntranceType" @change="handleEntranceTypeChange">
                  <option value="all">All Entrances</option>
                  <option value="withAppointment">With Appointment</option>
                </select>
                <i class="fa-solid fa-chevron-down dropdown-icon"></i>
              </div>
              <div v-if="selectedTimeframe === 'custom'" class="date-range-picker">
                <div class="date-field">
                  <label for="dashboard-start-date">Start</label>
                  <input id="dashboard-start-date" v-model="customStartDate" type="date"
                    @change="handleDateRangeChange" />
                </div>
                <span class="date-range-separator">to</span>
                <div class="date-field">
                  <label for="dashboard-end-date">End</label>
                  <input id="dashboard-end-date" v-model="customEndDate" type="date" @change="handleDateRangeChange" />
                </div>
              </div>
              <button @click="generateReport" class="btn-generate">
                <i class="fa-solid fa-file-export"></i> Generate Report
              </button>
            </div>
          </div>
        </div>

        <!-- Metrics Row - exact match to image -->
        <div v-if="loaded && selectedEntranceType !== 'withAppointment'" class="metrics-row">
          <!-- Daily Utilization -->
          <div class="metric-card shadow-sm">
            <div class="metric-content">
              <div class="metric-header">
                <span class="metric-label">Total Utilization</span>
                <div class="metric-header-actions">
                  <button class="metric-eye-btn" @click="openUtilizationModal" title="View overbooking details">
                    <i class="fa-solid fa-eye"></i>
                  </button>
                  <i class="fa-solid fa-chart-simple metric-chart-icon"></i>
                </div>
              </div>
              <div class="metric-body">
                <div class="main-val">
                  <div class="utilization-rate-group">
                    <span class="number">{{
                      formatMetric(metrics?.totalUtilization?.value)
                    }}</span>
                    <div class="metric-side-meta">
                      <!-- <span class="metric-side-label">Success Rate</span> -->
                      <span :class="[
                        'trend',
                        'utilization-trend',
                        metrics?.totalUtilization?.trend?.direction,
                      ]" :title="`Percentage change compared to ${selectedTimeframe === 'daily'
                        ? 'yesterday'
                        : selectedTimeframe === 'weekly'
                          ? 'last week'
                          : 'last month'
                        }`">
                        <i :class="getTrendIcon(
                          metrics?.totalUtilization?.trend?.direction,
                        )
                          "></i>
                        {{ metrics?.totalUtilization?.trend?.percentage }}
                      </span>
                    </div>
                  </div>
                </div>
                <div v-if="metrics?.totalUtilization?.comparison" class="sub-val">
                  <span class="vs-text-sub">{{
                    metrics.totalUtilization.comparison.mainText
                  }}</span>
                  <span class="separator"> • </span>
                  <span :class="[
                    'mini-trend',
                    metrics?.totalUtilization?.comparison?.yesterday
                      ?.direction === 'down'
                      ? 'down'
                      : 'up',
                  ]">
                    {{
                      metrics?.totalUtilization?.comparison?.yesterday?.value
                    }}
                    yesterday
                  </span>
                  <span class="separator">, </span>
                  <span :class="[
                    'mini-trend',
                    metrics?.totalUtilization?.comparison?.lastWeek
                      ?.direction === 'down'
                      ? 'down'
                      : 'up',
                  ]">
                    {{ metrics?.totalUtilization?.comparison?.lastWeek?.value }}
                    last week
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Month-to-Date Revenue -->
          <div class="metric-card shadow-sm">
            <div class="metric-content">
              <div class="metric-header">
                <span class="metric-label">{{ getRevenueLabel() }}</span>
                <i class="fa-solid fa-chart-simple metric-chart-icon"></i>
              </div>
              <div class="metric-body">
                <div class="main-val">
                  <div class="metric-value-group">
                    <span class="number">{{
                      formatCurrency(metrics?.revenue?.value)
                    }}</span>
                    <div class="metric-side-meta">
                      <!-- <span class="metric-side-label">Revenue</span> -->
                      <span :class="['trend', metrics?.revenue?.trend?.direction]" :title="`Percentage change compared to ${selectedTimeframe === 'daily'
                        ? 'yesterday'
                        : selectedTimeframe === 'weekly'
                          ? 'last week'
                          : 'last month'
                        }`">
                        <i :class="getTrendIcon(metrics?.revenue?.trend?.direction)
                          "></i>
                        {{ metrics?.revenue?.trend?.percentage }}
                      </span>
                    </div>
                  </div>
                </div>
                <div v-if="metrics?.revenue?.comparison" class="sub-val">
                  <span :class="[
                    'mini-trend',
                    metrics?.revenue?.comparison?.yesterday?.direction ===
                      'down'
                      ? 'down'
                      : 'up',
                  ]">
                    {{ metrics?.revenue?.comparison?.yesterday?.value }}
                    yesterday
                  </span>
                  <span class="separator">, </span>
                  <span :class="[
                    'mini-trend',
                    metrics?.revenue?.comparison?.lastWeek?.direction ===
                      'down'
                      ? 'down'
                      : 'up',
                  ]">
                    {{ metrics?.revenue?.comparison?.lastWeek?.value }} last
                    week
                  </span>
                </div>
                <div class="revenue-disclaimer">
                  <i class="fa-solid fa-circle-info"></i>
                  <strong style="margin-left: 6px">Estimated revenue:</strong>
                  ~$50 per appointment (approximate value).
                </div>
              </div>
            </div>
          </div>

          <!-- Upcoming Appointments -->
          <div class="metric-card shadow-sm">
            <div class="metric-content">
              <div class="metric-header">
                <span class="metric-label">{{ getAppointmentsLabel() }}</span>
                <i class="fa-solid fa-chart-simple metric-chart-icon"></i>
              </div>
              <div class="metric-body">
                <div class="main-val">
                  <div class="metric-value-group">
                    <span class="number">{{
                      metrics?.appointments?.value
                    }}</span>
                    <div class="metric-side-meta">
                      <!-- <span class="metric-side-label">Appointments</span> -->
                      <span :class="[
                        'trend',
                        metrics?.appointments?.trend?.direction,
                      ]" :title="`Percentage change compared to ${selectedTimeframe === 'daily'
                        ? 'yesterday'
                        : selectedTimeframe === 'weekly'
                          ? 'last week'
                          : 'last month'
                        }`">
                        <i :class="getTrendIcon(
                          metrics?.appointments?.trend?.direction,
                        )
                          "></i>
                        {{ metrics?.appointments?.trend?.percentage }}
                      </span>
                    </div>
                  </div>
                </div>
                <div v-if="metrics?.appointments?.comparison" class="sub-val">
                  <span :class="[
                    'mini-trend',
                    metrics?.appointments?.comparison?.yesterday
                      ?.direction === 'down'
                      ? 'down'
                      : 'up',
                  ]">
                    {{ metrics?.appointments?.comparison?.yesterday?.value }}
                    yesterday
                  </span>
                  <span class="separator">, </span>
                  <span :class="[
                    'mini-trend',
                    metrics?.appointments?.comparison?.lastWeek?.direction ===
                      'down'
                      ? 'down'
                      : 'up',
                  ]">
                    {{ metrics?.appointments?.comparison?.lastWeek?.value }}
                    last week
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Confirmed Appointments -->
          <div class="metric-card shadow-sm">
            <div class="metric-content">
              <div class="metric-header">
                <span class="metric-label">Total Active Appointments</span>
              </div>
              <div class="metric-body">
                <div class="main-val">
                  <div class="metric-value-group">
                    <span class="number">{{ metrics?.confirmed?.value }}</span>
                    <div class="metric-side-meta">
                      <!-- <span class="metric-side-label">Confirmed</span> -->
                      <span :class="['trend', metrics?.confirmed?.trend?.direction]" :title="`Percentage change compared to ${selectedTimeframe === 'daily'
                        ? 'yesterday'
                        : selectedTimeframe === 'weekly'
                          ? 'last week'
                          : 'last month'
                        }`">
                        <i :class="getTrendIcon(metrics?.confirmed?.trend?.direction)
                          "></i>
                        {{ metrics?.confirmed?.trend?.percentage }}
                      </span>
                    </div>
                  </div>
                </div>
                <div v-if="metrics?.confirmed?.comparison" class="sub-val">
                  <span :class="[
                    'mini-trend',
                    metrics?.confirmed?.comparison?.yesterday?.direction ===
                      'down'
                      ? 'down'
                      : 'up',
                  ]">
                    {{ metrics?.confirmed?.comparison?.yesterday?.value }}
                    yesterday
                  </span>
                  <span class="separator">, </span>
                  <span :class="[
                    'mini-trend',
                    metrics?.confirmed?.comparison?.lastWeek?.direction ===
                      'down'
                      ? 'down'
                      : 'up',
                  ]">
                    {{ metrics?.confirmed?.comparison?.lastWeek?.value }} last
                    week
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Skeleton Metrics Row -->
        <div v-else-if="selectedEntranceType !== 'withAppointment'" class="metrics-row skeleton-container">
          <div v-for="i in 4" :key="i" class="metric-card shadow-sm skeleton-card">
            <div class="skeleton-header">
              <div class="skeleton-animated skeleton-label"></div>
              <div class="skeleton-animated skeleton-icon"></div>
            </div>
            <div class="skeleton-body">
              <div class="skeleton-animated skeleton-main"></div>
              <div class="skeleton-animated skeleton-sub"></div>
            </div>
          </div>
        </div>

        <!-- Activity Overview Header (exact) -->
        <div class="overview-header">
          <h2 class="section-heading">Activity Overview</h2>
          <div class="overview-actions">
            <!-- <a
              class="btn-add-appt"
              href="https://dev.mamabots.com/guest-appointment-flow"
              target="_blank"
            >
              <i class="fa-solid fa-plus"></i> Add Appointment
            </a> -->
          </div>
        </div>

        <!-- Main Dashboard Grid - two columns -->
        <div v-if="loaded" class="dashboard-grid">
          <!-- Real content here (already existing in file) -->
          <div class="dashboard-col left">
            <!-- Appointment Utilization -->
            <div class="chart-card shadow-sm">
              <div class="card-header">
                <h3>
                  <i class="fa-solid fa-chart-simple card-header-icon"></i>
                  Appointment Utilization
                  <span class="count-badge">{{ utilizationStats.total }}</span>
                </h3>
                <div class="card-controls">
                  <i class="fa-solid fa-calendar-days"></i>
                  <i class="fa-solid fa-arrow-trend-up accent-icon"></i>
                </div>
              </div>
              <div class="chart-box">
                <BarChart v-if="loaded" :key="utilizationChartKey" :data="utilizationBarData"
                  :options="utilizationBarOptions" />
              </div>
              <!-- Three stats row -->
              <div class="chart-bottom-stats-grid">
                <div v-for="stat in [
                  'Total',
                  'Active',
                  'Completed',
                  'Approved',
                  'Open',
                  'Cancelled',
                ]" :key="stat" class="stat-col">
                  <span class="stat-label">{{ stat === "Approved" ? "Upcoming" : stat }}</span>
                  <div class="stat-main">
                    <span class="stat-val">{{
                      utilizationStats?.[
                      stat.toLowerCase() === "total"
                        ? "totalAppointments"
                        : stat.toLowerCase()
                      ] || 0
                    }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Today's Schedule -->
            <div class="schedule-card shadow-sm">
              <div class="card-header">
                <h3>
                  <i class="fa-solid fa-clock card-header-icon"></i>
                  {{ getScheduleTitle() }}
                </h3>
                <router-link to="/manage-appointments" class="schedule-filter-v2"><span>View
                    Details</span></router-link>
              </div>
              <div class="schedule-table">
                <div class="schedule-head-v3">
                  <span>Patient Name</span>
                  <span>Time</span>
                  <span>Date</span>
                  <span>Doctor</span>
                </div>
                <div v-for="(appointment, index) in scheduleData" :key="index" class="schedule-row-v3">
                  <div class="schedule-cell patient-cell">
                    {{ appointment.patientName }}
                  </div>
                  <div class="schedule-cell">{{ appointment.time }}</div>
                  <div class="schedule-cell">{{ appointment.date }}</div>
                  <div class="schedule-cell">{{ appointment.doctorName }}</div>
                </div>
              </div>
            </div>
          </div>

          <div class="dashboard-col right">
            <!-- Monthly Revenue & Collections -->
            <div class="chart-card shadow-sm">
              <div class="card-header">
                <h3>
                  <i class="fa-solid fa-chart-column card-header-icon"></i>
                  {{ getRevenueChartTitle() }}
                </h3>
                <div class="info-tooltip-container">
                  <i class="fa-solid fa-circle-info card-header-info"></i>
                  <div class="tooltip-content shadow-sm">
                    <p>
                      <strong>Revenue:</strong> Represent collected payments
                      from completed appointments.
                    </p>
                  </div>
                </div>
              </div>
              <div class="chart-box">
                <BarChart v-if="loaded" :data="revenueChartData" :options="getRevenueChartOptions()" />
              </div>
              <div class="revenue-summary">
                <div class="revenue-summary-card">
                  <div class="revenue-summary-header">
                    <div class="revenue-label-group">
                      <span class="revenue-label-dot"></span>
                      <span class="revenue-label-text">Revenue</span>
                    </div>
                    <span class="revenue-period-chip">{{
                      getRevenuePeriodChipLabel()
                    }}</span>
                  </div>
                  <div class="revenue-summary-value">
                    {{ formatCompactCurrency(revenueStats.revenue) }}
                  </div>
                  <div class="revenue-summary-note">
                    Collected payments shown across the selected time period.
                  </div>
                </div>
              </div>
            </div>

            <!-- Doctors Allocation -->
            <div class="chart-card shadow-sm">
              <div class="card-header">
                <h3>
                  <i class="fa-solid fa-credit-card card-header-icon"></i>
                  Doctors Allocation
                </h3>
                <i class="fa-solid fa-arrow-trend-up accent-icon"></i>
              </div>
              <div class="payments-container v2">
                <div class="payments-info">
                  <div class="allocation-summary">
                    <div class="appointments-summary-chip">
                      <span class="appointments-summary-chip-label">Appointments</span>
                      <span class="appointments-summary-chip-value">{{
                        allocationStats.total
                      }}</span>
                    </div>
                    <div class="allocated-doctors-summary">
                      <!-- <div class="allocated-doctors-icon">
                        <i class="fa-solid fa-user-doctor"></i>
                      </div> -->
                      <div class="allocated-doctors-content">
                        <span class="allocated-doctors-count">
                          {{
                            allocationStats.allocatedDoctors ||
                            allocationStats.items.length ||
                            0
                          }}
                        </span>
                        <span class="allocated-doctors-label">Allocated Doctors</span>
                      </div>
                    </div>
                  </div>
                  <ul class="payment-stats-list-v2">
                    <li v-for="(item, index) in allocationStats.items.slice(0, 6)" :key="index">
                      <span :class="['dot-v2', item.color]"></span>
                      <span class="label">{{ item.label }}</span>
                    </li>
                    <li v-if="allocationStats.items.length > 7" class="others-row">
                      <span class="others-badge">+{{ allocationStats.items.length - 6 }} others</span>
                    </li>
                  </ul>
                  <!-- <div class="total-summary-label">Total: {{ allocationStats.total }}</div> -->
                </div>
                <div class="donut-chart-wrapper">
                  <PieChart v-if="loaded" :data="paymentPieData" :options="pieOptions" />
                </div>
              </div>
              <div class="card-footer-v2">
                <button class="btn-primary-blue" style="width: 100%; justify-content: center"
                  @click="openDoctorAllocationModal('details')">
                  <i class="fa-solid fa-square-poll-vertical"></i> View Details
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Skeleton Dashboard Grid -->
        <div v-else class="dashboard-grid skeleton-container">
          <div class="dashboard-col left">
            <div v-for="i in 2" :key="i" class="chart-card shadow-sm skeleton-card full-height">
              <div class="skeleton-header">
                <div class="skeleton-animated skeleton-label-lg"></div>
              </div>
              <div class="skeleton-body-content">
                <div class="skeleton-animated skeleton-graph-placeholder"></div>
                <div class="skeleton-animated skeleton-text-row"></div>
              </div>
            </div>
          </div>
          <div class="dashboard-col right">
            <div v-for="i in 2" :key="i" class="chart-card shadow-sm skeleton-card full-height">
              <div class="skeleton-header">
                <div class="skeleton-animated skeleton-label-lg"></div>
              </div>
              <div class="skeleton-body-content">
                <div class="skeleton-animated skeleton-graph-placeholder"></div>
                <div class="skeleton-animated skeleton-text-row"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="footer-container">
        <FooterView />
      </div>
    </main>

    <!-- Doctor Allocation Modal -->
    <div v-if="showDoctorModal" class="modal-overlay" @click="closeDoctorModal">
      <div class="modal-container" @click.stop>
        <div class="modal-header">
          <h2>{{ modalTitle }}</h2>
          <button class="modal-close" @click="closeDoctorModal">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <!-- Summary Cards -->
          <div class="modal-summary-cards">
            <div class="summary-card">
              <div class="summary-icon">
                <i class="fa-solid fa-user-doctor"></i>
              </div>
              <div class="summary-content">
                <span class="summary-label">Total Doctors</span>
                <span class="summary-value">{{
                  doctorAllocationDetails.totalDoctors
                }}</span>
              </div>
            </div>
            <div class="summary-card">
              <div class="summary-icon">
                <i class="fa-solid fa-calendar-check"></i>
              </div>
              <div class="summary-content">
                <span class="summary-label">Total Appointments</span>
                <span class="summary-value">{{
                  doctorAllocationDetails.totalAppointments
                }}</span>
              </div>
            </div>
          </div>

          <!-- Time Period Filter -->
          <!-- <div class="modal-filter">
            <label>Time Period:</label>
            <select v-model="modalTimeframe" @change="updateModalData">
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div> -->

          <!-- Doctors Allocation Table -->
          <div class="modal-table-container">
            <table class="modal-table">
              <thead>
                <tr>
                  <th>Doctor</th>
                  <th>Specialization</th>
                  <th>Appointments</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(doctor, index) in doctorAllocationDetails.doctors" :key="index">
                  <td>
                    <div class="doctor-info">
                      <span :class="['dot-v2', doctor.color]" style="margin-right: 8px"></span>
                      <img v-if="getDoctorAvatar(doctor)" :src="getDoctorAvatar(doctor)"
                        :alt="doctor.fullName || 'Unnamed Doctor'" class="doctor-avatar" />
                      <span>{{ doctor.fullName || "Unnamed Doctor" }}</span>
                    </div>
                  </td>
                  <td>{{ doctor.speciality || "N/A" }}</td>
                  <td>{{ doctor.appointmentCount }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Collections Breakdown removed as per API response refinement -->
        </div>

        <div class="modal-footer">
          <button class="btn-secondary" @click="closeDoctorModal">Close</button>
          <!-- <button class="btn-primary" @click="exportDoctorData">Export Data</button> -->
        </div>
      </div>
    </div>

    <!-- Utilization Details Modal -->
    <div v-if="showUtilizationModal" class="modal-overlay" @click="closeUtilizationModal">
      <div class="modal-container utilization-modal" @click.stop>
        <div class="modal-header">
          <div class="modal-header-left">
            <div class="modal-header-icon">
              <i class="fa-solid fa-chart-simple"></i>
            </div>
            <div>
              <h2>Utilization Details <span style="font-size: 18px; font-weight: 500;">(Utilization Rate: {{
                utilizationDetails?.utilizationRate }}%)</span></h2>
              <p class="modal-subtitle">
                Slot utilization &amp; overbooking analysis
              </p>
            </div>
          </div>
          <button class="modal-close" @click="closeUtilizationModal">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <!-- Top KPI Cards -->
          <!-- Loading State -->
          <div v-if="utilizationLoading" class="util-skeleton-body">
            <div class="util-kpi-grid">
              <div v-for="i in 6" :key="i" class="util-kpi-card skeleton-animated"></div>
            </div>
            <div class="util-skeleton-chart">
              <div class="skeleton-animated skeleton-label-lg"></div>
              <div v-for="i in 3" :key="i" class="skeleton-animated skeleton-bar-row"></div>
            </div>
            <div class="util-skeleton-table">
              <div class="skeleton-animated skeleton-label-lg"></div>
              <div v-for="i in 4" :key="i" class="skeleton-animated skeleton-table-row"></div>
            </div>
          </div>

          <!-- Content State -->
          <div v-else>
            <!-- Top KPI Cards -->
            <div class="util-kpi-grid">
              <div class="util-kpi-card">
                <div class="util-kpi-icon blue">
                  <i class="fa-solid fa-calendar-check"></i>
                </div>
                <div class="util-kpi-content">
                  <span class="util-kpi-label">Total Slots</span>
                  <span class="util-kpi-value">{{ utilizationDetails.totalAvailable || 0 }}</span>
                </div>
              </div>
              <div class="util-kpi-card">
                <div class="util-kpi-icon green">
                  <i class="fa-solid fa-calendar-days"></i>
                </div>
                <div class="util-kpi-content">
                  <span class="util-kpi-label">Available Slots</span>
                  <span class="util-kpi-value">{{ this.remainingSlots }}</span>
                </div>
              </div>
              <div class="util-kpi-card">
                <div class="util-kpi-icon orange">
                  <i class="fa-solid fa-bookmark"></i>
                </div>
                <div class="util-kpi-content">
                  <span class="util-kpi-label">Booked Slots</span>
                  <span class="util-kpi-value">{{
                    utilizationDetails.totalBooked
                  }}</span>
                </div>
              </div>
              <div class="util-kpi-card">
                <div class="util-kpi-icon red">
                  <i class="fa-solid fa-triangle-exclamation"></i>
                </div>
                <div class="util-kpi-content">
                  <span class="util-kpi-label">Overbooked Slots</span>
                  <span class="util-kpi-value">{{
                    utilizationDetails.details.overbookedSlots
                  }}</span>
                </div>
              </div>
              <div class="util-kpi-card">
                <div class="util-kpi-icon purple">
                  <i class="fa-solid fa-users"></i>
                </div>
                <div class="util-kpi-content">
                  <span class="util-kpi-label">Overbooked Appointments</span>
                  <span class="util-kpi-value">{{
                    utilizationDetails.details.totalOverbookedAppointments
                  }}</span>
                </div>
              </div>
              <div class="util-kpi-card">
                <div class="util-kpi-icon teal">
                  <i class="fa-solid fa-layer-group"></i>
                </div>
                <div class="util-kpi-content">
                  <span class="util-kpi-label">Max Overbook Per Slot</span>
                  <span class="util-kpi-value">{{
                    utilizationDetails.details.maxOverbookingPerSlot
                  }}</span>
                </div>
              </div>
            </div>

            <!-- Note -->
            <div v-if="utilizationDetails.note" class="util-note">
              <i class="fa-solid fa-circle-info"></i>
              {{ utilizationDetails.note }}
            </div>

            <!-- Doctor Overbooking Section -->
            <div v-if="
              utilizationDetails.details.doctorOverbooking &&
              utilizationDetails.details.doctorOverbooking.length > 0
            " class="util-section">
              <h3 class="util-section-title">
                <i class="fa-solid fa-user-doctor"></i>
                Doctor Overbooking <span style="font-size: 14px; font-weight: 500;">(0 overbooking to {{
                  utilizationDetails.details.totalOverbookedAppointments
                }} overbooking)</span>
              </h3>
              <div class="util-doctor-chart">
                <div v-for="(doc, idx) in utilizationDetails.details
                  .doctorOverbooking" :key="doc.doctorId || idx" class="util-doctor-row">
                  <div class="util-doctor-name">{{ doc.doctorName }}</div>
                  <div class="util-bar-track">
                    <div class="util-bar-fill" :style="{
                      width: getOverbookBarWidth(getOverbookedSlotExcessCount(doc.overbookedSlotDetails)) + '%',
                    }"></div>
                  </div>
                  <div class="util-doctor-stats">
                    <span class="util-badge overbooked">{{ doc.overbookedSlots }} slots</span>
                    <span class="util-badge excess">+{{ getOverbookedSlotExcessCount(doc.overbookedSlotDetails) }}
                      excess</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Overbooked Slot Details (flattened from all doctors) -->
            <div v-if="allOverbookedSlotDetails.length > 0" class="util-section">
              <h3 class="util-section-title">
                <i class="fa-solid fa-clock-rotate-left"></i>
                Overbooked Slot Details
                <span class="util-kpi-label" style="margin-left: 6px; font-size: 12px">({{
                  allOverbookedSlotDetails.length }} slot{{
                    allOverbookedSlotDetails.length !== 1 ? "s" : ""
                  }})</span>
              </h3>
              <div class="modal-table-container" style="margin-bottom: 0">
                <table class="modal-table util-slot-table">
                  <thead>
                    <tr>
                      <th>Doctor</th>
                      <th>Date</th>
                      <th>Time Slot</th>
                      <th>Capacity</th>
                      <th>Bookings</th>
                      <th>Excess</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(slot, idx) in allOverbookedSlotDetails" :key="slot.slotId || idx">
                      <td>
                        <div class="doctor-info">
                          <img v-if="getDoctorAvatar(slot)" :src="getDoctorAvatar(slot)"
                            :alt="slot.fullName || 'Doctor'" class="doctor-avatar" />
                          {{ slot.doctorName }}
                        </div>
                      </td>
                      <td>{{ formatSlotDate(slot.date) }}</td>
                      <td>
                        <span class="util-time-chip">{{ formatTimeDisplay(slot.startTime) }} –
                          {{ formatTimeDisplay(slot.endTime) }}</span>
                      </td>
                      <td>{{ slot.capacity }}</td>
                      <td>
                        <span class="util-badge overbooked">{{
                          slot.bookings
                        }}</span>
                      </td>
                      <td>
                        <span class="util-badge excess">+{{ slot.excessCount }}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Empty state when no overbooking -->
            <div v-if="
              !utilizationDetails.details.doctorOverbooking ||
              utilizationDetails.details.doctorOverbooking.length === 0
            " class="util-empty">
              <i class="fa-solid fa-circle-check"></i>
              <p>No overbooking detected in this period.</p>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="closeUtilizationModal">
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
  <!-- end .dashboard-layout -->
</template>

<script>
import Header from "@/layout/Header.vue";
// Sidebar import removed
import FooterView from "@/layout/Footer.vue";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Filler,
} from "chart.js";
import { Bar as BarChart, Pie as PieChart } from "vue-chartjs";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Filler,
);
import { AnalyticsService } from "@/services/analytics.service";
import { AppointmentService } from "@/services/appointments.service";
import { AppointmentTypeService } from "@/services/appointmentType.service";

export default {
  name: "AppointmentDashboard",
  components: {
    Header,
    FooterView,
    BarChart,
    PieChart,
  },
  data() {
    return {
      loaded: false,
      selectedTimeframe: "weekly", // Default to weekly
      selectedEntranceType: "all",
      customStartDate: "",
      customEndDate: "",
      showDoctorModal: false,
      showUtilizationModal: false,
      utilizationLoading: false,
      modalViewType: "details", // 'details' or 'collections'
      modalTimeframe: "weekly",
      utilizationDetails: {
        utilizationRate: 0,
        totalAvailable: 0,
        totalBooked: 0,
        yesterdayChange: "0%",
        lastWeekChange: "0%",
        details: {
          overbookedSlots: 0,
          totalOverbookedAppointments: 0,
          maxOverbookingPerSlot: 0,
          doctorOverbooking: [
            {
              doctorId: "",
              doctorName: "",
              overbookedSlots: 0,
              excessAppointments: 0,
              overbookedSlotDetails: [
                {
                  slotId: "",
                  doctorId: "",
                  doctorName: "",
                  date: "",
                  startTime: "",
                  endTime: "",
                  capacity: 0,
                  bookings: 0,
                  excessCount: 0,
                },
              ],
            },
          ],
        },
        note: "Timeslot utilization: (booked slots / available slots) × 100. Overbooking tracked at slot level.",
      },
      metrics: {
        totalUtilization: {},
        revenue: {},
        appointments: {},
        confirmed: {},
      },
      utilizationStats: {
        total: 0,
        completed: 0,
        inProgress: 0,
        bookedSlots: 0,
        yesterdayChange: "0%",
        lastWeekChange: "0%",
      },

      revenueStats: {
        revenue: 0,
        pending: 0,
      },
      allocationStats: {
        total: 0,
        allocatedDoctors: 0,
        items: [],
      },
      doctorAllocationRawData: null,
      utilizationChartRawData: null,
      utilizationChartKey: 0,
      scheduleData: [],
      appointmentReasons: [],
      doctorAllocationDetails: {
        totalDoctors: 0,
        totalAppointments: 0,
        totalRevenue: 0,
        doctors: [],
        collections: {
          collected: 0,
          collectedPercent: 0,
          pastDue: 0,
          pastDuePercent: 0,
          overdue: 0,
          overduePercent: 0,
        },
      },
      barOptions: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: "index",
          intersect: false,
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: true,
            filter: (context) => {
              const value = context.parsed?.y ?? context.raw ?? 0;
              return Number(value) > 0;
            },
            callbacks: {
              label: (context) => {
                const label = context.dataset.label || "Value";
                const value = context.parsed.y !== null ? context.parsed.y : 0;
                return `${label}: ${this.formatCurrency(value)}`;
              },
            },
          },
        },
        scales: {
          y: {
            grid: { color: "#f1f5f9", drawBorder: false },
            ticks: {
              color: "#94a3b8",
              font: { size: 13 },
              callback: (val) => `${val}`,
            },
          },
          x: {
            grid: { display: false },
            ticks: { color: "#94a3b8", font: { size: 13 } },
          },
        },
      },
      utilizationBarOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: true,
            callbacks: {
              label: (context) => {
                const label = context.dataset.label || "Utilization";
                const value = context.parsed.y !== null ? context.parsed.y : 0;
                return `${label}: ${value}%`;
              },
            },
          },
        },
        scales: {
          y: {
            grid: { color: "#f1f5f9", drawBorder: false },
            ticks: {
              color: "#94a3b8",
              font: { size: 13 },
              callback: (val) => `${val}%`,
            },
            beginAtZero: true,
            suggestedMax: 10,
            title: {
              display: false,
            },
          },
          x: {
            grid: { display: false },
            ticks: {
              color: "#94a3b8",
              font: { size: 13 },
              autoSkip: false,
              maxRotation: 0,
              minRotation: 0,
              padding: 10,
            },
            title: {
              display: true,
              text: "Periods",
              color: "#64748b",
              font: { size: 12 },
            },
          },
        },
      },
      pieOptions: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "50%",
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: true,
            callbacks: {
              label: function (context) {
                const label = context.label || "";
                const value = context.raw || 0;
                return `${label}: ${value} appointments`;
              },
            },
          },
        },
      },
    };
  },
  computed: {
    remainingSlots() {
      return Math.abs(
        (this.utilizationDetails.totalAvailable || 0) -
        (this.utilizationDetails.totalBooked || 0)
      );
    },
    modalTitle() {
      return "Doctor Allocation Details";
    },
    allOverbookedSlotDetails() {
      const doctors = this.utilizationDetails?.details?.doctorOverbooking || [];
      return doctors.flatMap((doc) =>
        (doc.overbookedSlotDetails || []).map((slot) => ({
          ...slot,
          doctorName: slot.doctorName || doc.doctorName,
          doctorProfileImage:
            slot.doctorProfileImage ||
            slot.photo ||
            doc.doctorProfileImage ||
            doc.photo ||
            "",
        })),
      );
    },
    // revenueChartData() {
    //   if (this.revenueChartRawData) {
    //     return {
    //       labels: this.revenueChartRawData.days,
    //       datasets: [
    //         {
    //           data: this.revenueChartRawData.amounts,
    //           backgroundColor: '#4dd0e1',
    //           borderRadius: 6
    //         },
    //         {
    //           data: this.revenueChartRawData.amounts.map(() => 0), // Pending collections not in API
    //           backgroundColor: '#42a5f5',
    //           borderRadius: 6
    //         }
    //       ]
    //     };
    //   }
    //   const data = this.getRevenueData();
    //   return {
    //     labels: data.labels,
    //     datasets: [
    //       {
    //         data: data.revenue,
    //         backgroundColor: '#4dd0e1',
    //         borderRadius: 6
    //       },
    //       {
    //         data: data.pending,
    //         backgroundColor: '#42a5f5',
    //         borderRadius: 6
    //       }
    //     ]
    //   };
    // },
    revenueChartData() {
      if (this.revenueChartRawData) {
        return {
          labels:
            this.revenueChartRawData.labels || this.revenueChartRawData.days,
          datasets: [
            {
              label: "Revenue",
              data: this.revenueChartRawData.amounts,
              backgroundColor: "#4dd0e1",
              borderRadius: 6,
            },
          ],
        };
      }

      // fallback while API loads
      return {
        labels: ["Mon", "Tue", "Wed", "Thu", "Fri"],
        datasets: [
          {
            label: "Revenue",
            data: [0, 0, 0, 0, 0],
            backgroundColor: "#4dd0e1",
            borderRadius: 6,
          },
        ],
      };
    },
    paymentPieData() {
      if (this.allocationStats && this.allocationStats.items.length > 0) {
        const colorMap = {
          cyan: "#00d2eb",
          orange: "#ffb74d",
          blue: "#007af8",
          red: "#ff7043",
          purple: "#6f42c1",
          lightCyan: "#4dd0e1",
          lightBlue: "#42a5f5",
          lavender: "#ba68c8",
        };

        return {
          labels: this.allocationStats.items.map(
            (i) => i.fullName || "Unknown Doctor",
          ),
          datasets: [
            {
              data: this.allocationStats.items.map(
                (i) => i.appointmentCount || 0,
              ),
              backgroundColor: this.allocationStats.items.map(
                (i) => colorMap[i.color] || "#cbd5e1",
              ),
              borderWidth: 0,
            },
          ],
        };
      }
      return {
        labels: [],
        datasets: [
          {
            data: [],
            backgroundColor: [],
            borderWidth: 0,
          },
        ],
      };
    },
    utilizationBarData() {
      // Definitive failsafe check
      console.log(
        "this.utilizationChartRawData",
        JSON.parse(JSON.stringify(this.utilizationChartRawData)),
      );
      if (!this.utilizationChartRawData) {
        return { labels: [], datasets: [] };
      }

      // Handle root-level or nested data property
      const source =
        this.utilizationChartRawData.data || this.utilizationChartRawData;

      console.log("source", JSON.parse(JSON.stringify(source)));

      const labels =
        source.labels || (source.days ? source.days.map((d) => d.day) : []);

      const percentages =
        source.percentages ||
        (source.days ? source.days.map((d) => d.percentage) : []);

      if (!labels || labels.length === 0) {
        return { labels: [], datasets: [] };
      }

      return {
        labels: labels,
        datasets: [
          {
            label: "Utilization",
            data: percentages,
            backgroundColor: "#007af8",
            borderRadius: 8,
            barThickness: "flex",
            maxBarThickness: 30,
          },
        ],
      };
    },
  },
  created() {
    this.initializeDashboardFilters();
    this.fetchAppointmentReasons();
    this.updateDashboardData();
    this.loadDoctorAllocationData();
  },
  methods: {
    getLocalDateString(date = new Date()) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    normalizeDateQueryValue(value, fallback) {
      return /^\d{4}-\d{2}-\d{2}$/.test(value || "") ? value : fallback;
    },

    normalizeEntranceType(value) {
      return ["withAppointment", "all"].includes(value) ? value : "all";
    },

    getAnalyticsFilterValue(timeframe = this.selectedTimeframe) {
      switch (timeframe) {
        case "daily":
          return "day";
        case "weekly":
          return "week";
        case "monthly":
          return "month";
        case "custom":
          return "custom";
        default:
          return "week";
      }
    },

    initializeDashboardFilters() {
      const today = this.getLocalDateString();
      const query = this.$route?.query || {};
      const timeframe = ["daily", "weekly", "monthly", "custom"].includes(
        query.timeframe,
      )
        ? query.timeframe
        : query.filter === "custom"
          ? "custom"
          : this.selectedTimeframe;

      this.selectedTimeframe = timeframe;
      this.modalTimeframe = timeframe;
      this.selectedEntranceType = this.normalizeEntranceType(
        query.entranceType,
      );

      if (timeframe === "custom") {
        this.customStartDate = this.normalizeDateQueryValue(
          query.startDate,
          today,
        );
        this.customEndDate = this.normalizeDateQueryValue(query.endDate, today);
      } else {
        this.customStartDate = today;
        this.customEndDate = today;
      }

      if (this.customStartDate > this.customEndDate) {
        this.customEndDate = this.customStartDate;
      }

      this.syncDashboardQuery();
    },

    getEffectiveDateRange(timeframe = this.selectedTimeframe) {
      const today = this.getLocalDateString();

      if (timeframe !== "custom") {
        return {
          startDate: today,
          endDate: today,
        };
      }

      const startDate = this.normalizeDateQueryValue(
        this.customStartDate,
        today,
      );
      const endDate = this.normalizeDateQueryValue(
        this.customEndDate,
        startDate,
      );

      return startDate <= endDate
        ? { startDate, endDate }
        : { startDate, endDate: startDate };
    },

    getDashboardQueryParams(timeframe = this.selectedTimeframe) {
      const filter = this.getAnalyticsFilterValue(timeframe);
      const entranceType = this.normalizeEntranceType(this.selectedEntranceType);
      if (filter !== "custom") {
        return { filter, entranceType };
      }

      const { startDate, endDate } = this.getEffectiveDateRange(timeframe);

      return {
        filter,
        entranceType,
        startDate,
        endDate,
      };
    },

    getScheduleQueryParams(timeframe = this.selectedTimeframe) {
      const entranceType = this.normalizeEntranceType(this.selectedEntranceType);
      if (timeframe !== "custom") {
        return {
          page: 1,
          limit: 7,
          entranceType,
          period:
            timeframe === "monthly"
              ? "thisMonth"
              : timeframe === "weekly"
                ? "thisWeek"
                : "today",
        };
      }

      return {
        page: 1,
        limit: 7,
        entranceType,
        ...this.getDashboardQueryParams(timeframe),
        period: this.getDashboardQueryParams(timeframe).filter,
        filter: undefined
      };
    },

    syncDashboardQuery() {
      if (!this.$router || !this.$route) return;

      const filter = this.getAnalyticsFilterValue(this.selectedTimeframe);
      const query = {
        ...this.$route.query,
        timeframe: this.selectedTimeframe,
        filter,
        entranceType: this.normalizeEntranceType(this.selectedEntranceType),
      };

      if (filter === "custom") {
        const { startDate, endDate } = this.getEffectiveDateRange(
          this.selectedTimeframe,
        );
        query.startDate = startDate;
        query.endDate = endDate;
      } else {
        delete query.startDate;
        delete query.endDate;
      }

      const navigation = this.$router.replace({
        query,
      });

      if (navigation?.catch) {
        navigation.catch(() => { });
      }
    },

    refreshOpenModals() {
      if (this.showDoctorModal) {
        this.loadDoctorAllocationData();
      }

      if (this.showUtilizationModal) {
        this.fetchUtilizationDetails();
      }
    },

    generateReport() {
      const now = new Date();
      const dateString = now.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      const timeframeLabel =
        this.selectedTimeframe.charAt(0).toUpperCase() +
        this.selectedTimeframe.slice(1);
      const escapeHtml = (value) =>
        String(value ?? "")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#39;");

      const kpiRows = [
        {
          label: "Total Utilization",
          value: this.formatMetric(this.metrics?.totalUtilization?.value),
          trend: this.metrics?.totalUtilization?.trend?.percentage || "0%",
        },
        {
          label: "Revenue",
          value: this.formatCurrency(this.metrics?.revenue?.value || 0),
          trend: this.metrics?.revenue?.trend?.percentage || "0%",
        },
        {
          label: "Upcoming Appointments",
          value: this.metrics?.appointments?.value || 0,
          trend: this.metrics?.appointments?.trend?.percentage || "0%",
        },
        {
          label: "Successful Appointments",
          value: this.metrics?.confirmed?.value || 0,
          trend: this.metrics?.confirmed?.trend?.percentage || "0%",
        },
      ];

      const appointmentBreakdown = [
        [
          "Total",
          this.utilizationStats.totalAppointments ||
          this.utilizationStats.total ||
          0,
        ],
        ["Completed", this.utilizationStats.completed || 0],
        ["Approved", this.utilizationStats.approved || 0],
        ["Pending", this.utilizationStats.open || 0],
        ["Cancelled", this.utilizationStats.cancelled || 0],
      ];

      const doctorRows = this.allocationStats?.items?.length
        ? this.allocationStats.items
        : [];

      const scheduleRows = this.scheduleData?.length ? this.scheduleData : [];

      const html = `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>Mamabots Dashboard Report</title>
    <style>
      body {
        margin: 0;
        padding: 32px;
        font-family: Arial, sans-serif;
        background: #f4f7fb;
        color: #1e293b;
      }
      .report {
        max-width: 1100px;
        margin: 0 auto;
        background: #ffffff;
        border-radius: 20px;
        overflow: hidden;
        box-shadow: 0 20px 45px rgba(15, 23, 42, 0.08);
      }
      .hero {
        padding: 32px 36px;
        background: linear-gradient(135deg, #007af8 0%, #38bdf8 100%);
        color: white;
      }
      .hero h1 {
        margin: 0 0 8px;
        font-size: 32px;
      }
      .hero p {
        margin: 0;
        font-size: 15px;
        opacity: 0.92;
      }
      .section {
        padding: 28px 36px;
        border-top: 1px solid #e2e8f0;
      }
      .section h2 {
        margin: 0 0 18px;
        font-size: 20px;
        color: #0f172a;
      }
      .kpi-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 16px;
      }
      .kpi-card {
        padding: 18px;
        border: 1px solid #dbeafe;
        border-radius: 16px;
        background: #f8fbff;
      }
      .kpi-card .label {
        font-size: 13px;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: #64748b;
        margin-bottom: 8px;
      }
      .kpi-card .value {
        font-size: 28px;
        font-weight: 700;
        color: #0f172a;
        margin-bottom: 6px;
      }
      .kpi-card .trend {
        font-size: 14px;
        font-weight: 600;
        color: #007af8;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        font-size: 14px;
      }
      th {
        text-align: left;
        background: #eff6ff;
        color: #334155;
        font-size: 12px;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        padding: 12px 14px;
      }
      td {
        padding: 14px;
        border-bottom: 1px solid #e2e8f0;
      }
      tr:last-child td {
        border-bottom: none;
      }
      .empty {
        padding: 18px;
        border: 1px dashed #cbd5e1;
        border-radius: 12px;
        color: #64748b;
        background: #f8fafc;
      }
      @media (max-width: 768px) {
        body {
          padding: 16px;
        }
        .hero,
        .section {
          padding: 22px;
        }
        .kpi-grid {
          grid-template-columns: 1fr;
        }
      }
    </style>
  </head>
  <body>
    <div class="report">
      <div class="hero">
        <h1>Mamabots Dashboard Report</h1>
        <p>${escapeHtml(timeframeLabel)} view • Generated on ${escapeHtml(
        dateString,
      )}</p>
      </div>

      <div class="section">
        <h2>Key Performance Indicators</h2>
        <div class="kpi-grid">
          ${kpiRows
          .map(
            (item) => `
            <div class="kpi-card">
              <div class="label">${escapeHtml(item.label)}</div>
              <div class="value">${escapeHtml(item.value)}</div>
              <div class="trend">Trend: ${escapeHtml(item.trend)}</div>
            </div>`,
          )
          .join("")}
        </div>
      </div>

      <div class="section">
        <h2>Appointment Breakdown</h2>
        <table>
          <thead>
            <tr>
              <th>Status</th>
              <th>Count</th>
            </tr>
          </thead>
          <tbody>
            ${appointmentBreakdown
          .map(
            ([label, count]) => `
              <tr>
                <td>${escapeHtml(label)}</td>
                <td>${escapeHtml(count)}</td>
              </tr>`,
          )
          .join("")}
          </tbody>
        </table>
      </div>

      <div class="section">
        <h2>Doctor Allocation</h2>
        ${doctorRows.length
          ? `<table>
                <thead>
                  <tr>
                    <th>Doctor Name</th>
                    <th>Specialization</th>
                    <th>Appointments</th>
                  </tr>
                </thead>
                <tbody>
                  ${doctorRows
            .map(
              (item) => `
                    <tr>
                      <td>${escapeHtml(item.fullName || "Unknown")}</td>
                      <td>${escapeHtml(item.speciality || "N/A")}</td>
                      <td>${escapeHtml(item.appointmentCount || 0)}</td>
                    </tr>`,
            )
            .join("")}
                </tbody>
              </table>`
          : `<div class="empty">No doctor allocation data available.</div>`
        }
      </div>

      <div class="section">
        <h2>Schedule Details</h2>
        ${scheduleRows.length
          ? `<table>
                <thead>
                  <tr>
                    <th>Patient Name</th>
                    <th>Time</th>
                    <th>Date</th>
                    <th>Doctor</th>
                  </tr>
                </thead>
                <tbody>
                  ${scheduleRows
            .map(
              (item) => `
                    <tr>
                      <td>${escapeHtml(
                item.patientName || "Unknown Patient",
              )}</td>
                      <td>${escapeHtml(item.time || "")}</td>
                      <td>${escapeHtml(item.date || "")}</td>
                      <td>${escapeHtml(
                item.doctorName || "Unknown Doctor",
              )}</td>
                    </tr>`,
            )
            .join("")}
                </tbody>
              </table>`
          : `<div class="empty">No appointments scheduled for this timeframe.</div>`
        }
      </div>
    </div>
  </body>
</html>`;

      const blob = new Blob([html], { type: "text/html;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `mamabots_report_${this.selectedTimeframe}_${now.toISOString().split("T")[0]
        }.html`,
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },

    formatStatus(status) {
      const statusMap = {
        open: "Pending",
        approved: "Approved",
        cancelled: "Cancelled",
        completed: "Completed",
        started: "Checked In",
        inprogress: "In Progress",
        closed: "Closed",
      };
      return statusMap[status] || status;
    },

    convertToCSV(headers, rows) {
      const headerLine = headers.join(",");
      const rowLines = rows.map((row) => row.join(",")).join("\n");
      return `${headerLine}\n${rowLines}`;
    },
    handleTimeframeChange() {
      if (this.selectedTimeframe === "custom") {
        const { startDate, endDate } = this.getEffectiveDateRange("custom");
        this.customStartDate = startDate;
        this.customEndDate = endDate;
      } else {
        const today = this.getLocalDateString();
        this.customStartDate = today;
        this.customEndDate = today;
      }

      this.syncDashboardQuery();
      this.updateDashboardData();
      this.refreshOpenModals();
    },

    handleDateRangeChange() {
      const { startDate, endDate } = this.getEffectiveDateRange(
        this.selectedTimeframe,
      );
      this.customStartDate = startDate;
      this.customEndDate = endDate;

      this.syncDashboardQuery();
      this.updateDashboardData();
      this.refreshOpenModals();
    },

    handleEntranceTypeChange() {
      this.selectedEntranceType = this.normalizeEntranceType(
        this.selectedEntranceType,
      );
      this.syncDashboardQuery();
      this.updateDashboardData();
      this.refreshOpenModals();
    },

    async updateDashboardData() {
      this.loaded = false;
      try {
        await Promise.all([
          this.updateMetrics(),
          this.updateUtilizationStats(),
          this.updateRevenueStats(),
          this.updateAllocationStats(),
          this.updateScheduleData(),
        ]);
      } catch (error) {
        console.error("Error updating dashboard data:", error);
      } finally {
        this.loaded = true;
      }
    },

    // Utilization Modal
    openUtilizationModal() {
      this.showUtilizationModal = true;
      document.body.style.overflow = "hidden";
      this.fetchUtilizationDetails();
    },

    closeUtilizationModal() {
      this.showUtilizationModal = false;
      this.utilizationLoading = false;
      document.body.style.overflow = "auto";
    },

    normalizeDoctorOverbooking(doctorOverbooking) {
      if (Array.isArray(doctorOverbooking)) {
        return doctorOverbooking;
      }

      if (!doctorOverbooking) {
        return [];
      }

      if (typeof doctorOverbooking === "string") {
        try {
          const parsed = JSON.parse(doctorOverbooking);
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          console.warn(
            "Unable to parse doctorOverbooking payload:",
            doctorOverbooking,
            error,
          );
          return [];
        }
      }

      return [];
    },

    async fetchUtilizationDetails() {
      this.utilizationLoading = true;
      try {
        const res = await AnalyticsService.getUtilizationSummary(
          this.getDashboardQueryParams(),
        );
        // API may return data at res.data.data or res.data directly
        const data = res.data?.data || res.data;

        const hasDoctorOverbooking = Object.prototype.hasOwnProperty.call(
          data?.details || {},
          "doctorOverbooking",
        );
        const doctorOverbooking = this.normalizeDoctorOverbooking(
          data?.details?.doctorOverbooking,
        );

        if (data && data.utilizationRate !== undefined) {
          this.utilizationDetails = {
            utilizationRate:
              data.utilizationRate ?? this.utilizationDetails.utilizationRate,
            totalSlots: data.totalSlots ?? this.utilizationDetails.totalSlots,
            totalAvailable:
              data.totalAvailable ?? this.utilizationDetails.totalAvailable,
            totalBooked:
              data.totalBooked ?? this.utilizationDetails.totalBooked,
            yesterdayChange:
              data.yesterdayChange ?? this.utilizationDetails.yesterdayChange,
            lastWeekChange:
              data.lastWeekChange ?? this.utilizationDetails.lastWeekChange,
            note: data.note ?? this.utilizationDetails.note,
            details: {
              overbookedSlots:
                data.details?.overbookedSlots ??
                this.utilizationDetails.details.overbookedSlots,
              totalOverbookedAppointments:
                data.details?.totalOverbookedAppointments ??
                this.utilizationDetails.details.totalOverbookedAppointments,
              maxOverbookingPerSlot:
                data.details?.maxOverbookingPerSlot ??
                this.utilizationDetails.details.maxOverbookingPerSlot,
              // overbookedSlotDetails is now nested per-doctor inside doctorOverbooking
              doctorOverbooking: hasDoctorOverbooking
                ? doctorOverbooking
                : this.utilizationDetails.details.doctorOverbooking,
            },
          };
        }
      } catch (error) {
        console.error("Error fetching utilization details:", error);
        // Keep static fallback data
      } finally {
        this.utilizationLoading = false;
      }
    },

    getOverbookedSlotExcessCount(overbookedSlotDetails = []) {
      return overbookedSlotDetails.reduce(
        (total, slot) => total + (Number(slot.excessCount) || 0),
        0,
      );
    },

    getOverbookBarWidth(excessCount) {
      const max =
        Number(
          this.utilizationDetails?.details?.totalOverbookedAppointments,
        ) || 0;
      const value = Number(excessCount) || 0;

      if (max <= 0) return 0;

      return Math.min(100, Math.round((value / max) * 100));
    },

    parseLocalDate(value) {
      if (!value) return null;

      if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
      }

      if (typeof value === "string") {
        const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (match) {
          const [, year, month, day] = match;
          return new Date(Number(year), Number(month) - 1, Number(day));
        }
      }

      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    },

    formatSlotDate(dateStr) {
      if (!dateStr) return "";
      const d = this.parseLocalDate(dateStr);
      if (!d) return dateStr;
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    },

    formatTimeDisplay(time) {
      if (!time) return "";
      const localDate = this.convertToLocalTime(null, time);
      if (!localDate) return "";
      return localDate.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    },

    // Modal methods
    openDoctorAllocationModal(viewType) {
      this.modalViewType = viewType;
      this.modalTimeframe = this.selectedTimeframe;
      this.loadDoctorAllocationData();
      this.showDoctorModal = true;
      // Prevent body scrolling when modal is open
      document.body.style.overflow = "hidden";
    },

    closeDoctorModal() {
      this.showDoctorModal = false;
      // Restore body scrolling
      document.body.style.overflow = "auto";
    },

    updateModalData() {
      this.loadDoctorAllocationData();
    },

    async loadDoctorAllocationData() {
      try {
        const res = await AnalyticsService.getDoctorAllocation(
          this.getDashboardQueryParams(this.modalTimeframe),
        );
        const doctors = res.data.doctors || [];

        const colorNames = [
          "cyan",
          "orange",
          "blue",
          "red",
          "purple",
          "lightCyan",
          "lightBlue",
          "lavender",
        ];
        this.doctorAllocationDetails = {
          totalDoctors: doctors.length,
          totalAppointments: doctors.reduce(
            (sum, d) => sum + (d.appointmentCount || 0),
            0,
          ),
          doctors: doctors.map((d, index) => ({
            ...d,
            color: colorNames[index % colorNames.length],
          })),
        };
      } catch (error) {
        console.error("Error loading doctor allocation modal data:", error);
      }
    },

    exportDoctorData() {
      // Mock export functionality
      alert(`Exporting ${this.modalTitle} data as CSV...`);
      // In a real implementation, you would generate and download a CSV file
    },

    // Metrics methods
    async updateMetrics() {
      try {
        const [upcomingResponse, successfulResponse, revenueMTDResponse] =
          await Promise.all([
            AnalyticsService.getUpcomingAppointments(
              this.getDashboardQueryParams(),
            ),
            AnalyticsService.getSuccessfulAppointments(
              this.getDashboardQueryParams(),
            ),
            AnalyticsService.getRevenueMTD(this.getDashboardQueryParams()),
          ]);

        const upcoming = upcomingResponse.data;
        const successful = successfulResponse.data;
        const revenueMTD = revenueMTDResponse.data || {};

        this.metrics = {
          ...this.metrics, // Preserve existing metrics (like totalUtilization if it was set)
          revenue: {
            value: revenueMTD.currentAmount || 0,
            trend: {
              direction: (revenueMTD.yesterdayChange || "").includes("-")
                ? "down"
                : "up",
              percentage:
                (this.selectedTimeframe === "daily"
                  ? revenueMTD.yesterdayChange
                  : revenueMTD.lastWeekChange) || "0%",
              yesterdayChange: revenueMTD.yesterdayChange || "0%",
              lastWeekChange: revenueMTD.lastWeekChange || "0%",
            },
            comparison: {
              yesterday: {
                value: revenueMTD.yesterdayChange || "0%",
                direction: (revenueMTD.yesterdayChange || "").includes("-")
                  ? "down"
                  : "up",
              },
              lastWeek: {
                value: revenueMTD.lastWeekChange || "0%",
                direction: (revenueMTD.lastWeekChange || "").includes("-")
                  ? "down"
                  : "up",
              },
            },
          },

          appointments: {
            value: upcoming.count || 0,
            trend: {
              direction: (this.selectedTimeframe === "daily"
                ? upcoming.yesterdayChange || "0%"
                : upcoming.lastWeekChange || "0%"
              ).includes("-")
                ? "down"
                : "up",

              percentage:
                this.selectedTimeframe === "daily"
                  ? upcoming.yesterdayChange
                  : upcoming.lastWeekChange,

              yesterdayChange: upcoming.yesterdayChange,
              lastWeekChange: upcoming.lastWeekChange,
            },
            comparison: {
              yesterday: {
                value: upcoming.yesterdayChange || "0%",
                direction: (upcoming.yesterdayChange || "").includes("-")
                  ? "down"
                  : "up",
              },
              lastWeek: {
                value: upcoming.lastWeekChange || "0%",
                direction: (upcoming.lastWeekChange || "").includes("-")
                  ? "down"
                  : "up",
              },
            },
            scheduled: upcoming.count || 0,
            revenue: 0,
          },

          confirmed: {
            value: successful.count || 0,
            trend: {
              direction: (successful.lastWeekChange || "").startsWith("+")
                ? "up"
                : "down",
              percentage:
                (this.selectedTimeframe === "daily"
                  ? successful.yesterdayChange
                  : successful.lastWeekChange) || "0%",
            },
            comparison: {
              yesterday: {
                value: successful.yesterdayChange || "0%",
                direction: (successful.yesterdayChange || "").includes("-")
                  ? "down"
                  : "up",
              },
              lastWeek: {
                value: successful.lastWeekChange || "0%",
                direction: (successful.lastWeekChange || "").includes("-")
                  ? "down"
                  : "up",
              },
            },
            failedCharges: 0,
          },
        };
      } catch (error) {
        console.error("Error updating metrics:", error);
      }
    },

    async updateUtilizationStats() {
      try {
        const [graphResponse, summaryResponse] = await Promise.all([
          AnalyticsService.getUtilizationGraph(this.getDashboardQueryParams()),
          AnalyticsService.getUtilizationSummary(
            this.getDashboardQueryParams(),
          ),
        ]);

        // Update chart data
        const graphData = graphResponse.data?.data || graphResponse.data;
        this.utilizationChartRawData = graphData;

        // Force chart re-render
        this.$nextTick(() => {
          this.utilizationChartKey++;
        });

        // Map summary
        const summary = summaryResponse.data?.data || summaryResponse.data;

        this.utilizationStats = {
          ...summary,
          successRate: summary.successRate || 0,
          totalAppointments: summary.totalAppointments || 0,
          completed: summary.completed || 0,
          approved: summary.approved || 0,
          open: summary.open || 0,
          cancelled: summary.cancelled || 0,
          yesterdayChange: summary.yesterdayChange || "0%",
          lastWeekChange: summary.lastWeekChange || "0%",
          active: (summary.approved || 0) + (summary.completed || 0),
        };

        // Update top metric card
        this.metrics.totalUtilization = {
          value: `${summary.utilizationRate || 0}%`,
          trend: {
            direction: (summary.yesterdayChange || "").startsWith("-")
              ? "down"
              : "up",
            percentage:
              (this.selectedTimeframe === "daily"
                ? summary.yesterdayChange
                : summary.lastWeekChange) || "0%",
            yesterdayChange: summary.yesterdayChange || "0%",
            lastWeekChange: summary.lastWeekChange || "0%",
          },
          comparison: {
            mainText: `${summary.approved || 0} of ${summary.totalAppointments || 0
              } appointments upcoming`,
            yesterday: {
              value: summary.yesterdayChange || "0%",
              direction: (summary.yesterdayChange || "").startsWith("-")
                ? "down"
                : "up",
            },
            lastWeek: {
              value: summary.lastWeekChange || "0%",
              direction: (summary.lastWeekChange || "").startsWith("-")
                ? "down"
                : "up",
            },
          },
        };
      } catch (error) {
        console.error("Error updating utilization stats:", error);
      }
    },

    // async updateRevenueStats() {
    //   try {
    //     const res = await AnalyticsService.getMonthlyRevenue({ filter: 'current' });
    //     const data = res.data;

    //     // Map API data to component state
    //     // Note: The API returns { days: [], amounts: [] }
    //     this.revenueStats = {
    //       revenue: data.amounts.reduce((a, b) => a + b, 0),
    //       pending: 0 // Payment module integration pending as per API
    //     };

    //     // If we want to update the chart data too, we might need to store the raw data
    //     this.revenueChartRawData = data;
    //   } catch (error) {
    //     console.error("Error updating revenue stats:", error);
    //   }
    // },
    // async updateRevenueStats() {
    //   try {
    //     if (this.selectedTimeframe === "monthly") {
    //       const res = await AnalyticsService.getMonthlyRevenue({ filter: "month" });
    //       const data = res.data.data;

    //       this.revenueChartRawData = {
    //         days: data.days,
    //         amounts: data.amounts
    //       };

    //       this.revenueStats = {
    //         revenue: data.amounts.reduce((a, b) => a + b, 0),
    //         pending: 0
    //       };

    //     } else {
    //       // fallback for daily/weekly
    //       this.revenueChartRawData = null;

    //       const data = this.getRevenueStatsData();

    //       this.revenueStats = {
    //         revenue: data.revenue * 1000,
    //         pending: data.pending * 1000
    //       };
    //     }

    //   } catch (error) {
    //     console.error("Revenue fetch error:", error);
    //   }
    // },

    async updateRevenueStats() {
      try {
        const [chartRes, mtdRes] = await Promise.all([
          AnalyticsService.getMonthlyRevenue(this.getDashboardQueryParams()),
          AnalyticsService.getRevenueMTD(this.getDashboardQueryParams()),
        ]);

        const chartData = chartRes.data;
        const mtdData = mtdRes.data;

        this.revenueChartRawData = {
          labels: chartData.labels || chartData.days,
          amounts: chartData.amounts,
        };

        this.revenueStats = {
          revenue: mtdData.currentAmount || 0,
          pending: 0,
        };
      } catch (error) {
        console.error("Revenue fetch error:", error);
      }
    },

    async updateAllocationStats() {
      try {
        const res = await AnalyticsService.getDoctorAllocation(
          this.getDashboardQueryParams(),
        );
        const doctors = res.data.doctors || [];

        const colorNames = [
          "cyan",
          "orange",
          "blue",
          "red",
          "purple",
          "lightCyan",
          "lightBlue",
          "lavender",
        ];

        this.allocationStats = {
          total: doctors.reduce((sum, d) => sum + (d.appointmentCount || 0), 0),
          allocatedDoctors: doctors.length,
          items: doctors.map((d, index) => ({
            ...d,
            label: `${d.fullName || "Unknown"}: ${d.appointmentCount || 0
              } appts`,
            color: colorNames[index % colorNames.length],
          })),
        };
      } catch (error) {
        console.error("Error updating allocation stats:", error);
      }
    },

    async updateScheduleData() {
      try {
        const res = await AppointmentService.getList(
          this.getScheduleQueryParams(),
        );
        if (res.success && res.data) {
          this.scheduleData = this.formatAppointmentsForSchedule(res.data);
        } else {
          this.scheduleData = this.getScheduleDataByTimeframe(); // Fallback to mock
        }
      } catch (error) {
        console.error("Error updating schedule data:", error);
        this.scheduleData = this.getScheduleDataByTimeframe(); // Fallback to mock
      }
    },

    formatAppointmentsForSchedule(data) {
      const appointments = data?.appointments || data?.data || data || [];
      if (!Array.isArray(appointments)) return [];

      return appointments.map((app) => {
        const patientName = this.formatFullName(app.patient);
        const time = this.formatTime(app);

        // Map status to dots/classes as seen in AppointmentDashboard.vue
        // statuses: [{ class: 'cyan', active: true }, { class: 'gray', active: false }, { class: 'blue', active: true }]
        const statuses = [
          {
            class: "cyan",
            active: app.status === "approved" || app.status === "completed",
          },
          { class: "gray", active: app.status === "open" },
          { class: "blue", active: app.status === "completed" },
        ];

        return {
          patientName,
          time,
          date: this.formatScheduleDate(app),
          doctorName: this.formatScheduleDoctor(app.doctor),
          statuses,
        };
      });
    },

    async fetchAppointmentReasons() {
      try {
        const hospitalId =
          this.$store.state.user.currentUser?.objectId ||
          this.$store.state.user.currentUser?.hospitalId;
        if (!hospitalId) return;

        const response = await AppointmentTypeService.getAll({ hospitalId });
        const data = response?.data?.data;

        if (data) {
          this.appointmentReasons = (data.data || [])
            .map((t) => ({
              value: t?.id || t?.code || "",
              label: t?.label || "",
              description: t?.description || "",
              name: t?.name || "",
              code: t?.code || "",
            }))
            .filter(
              (i) => i.value && (i.label || i.description || i.name || i.code),
            );
        }
      } catch (error) {
        console.error("Error fetching appointment types:", error);
      }
    },

    getAppointmentTypeNameForSchedule(typeValue) {
      if (!typeValue) return "Appointment";

      const type = this.appointmentReasons.find(
        (item) =>
          item.value === typeValue ||
          item.code === typeValue ||
          item.label === typeValue ||
          item.description === typeValue ||
          item.name === typeValue,
      );

      return type
        ? type.description || type.label || type.name || type.code || typeValue
        : typeValue;
    },

    formatFullName(patient, isDoctor = false) {
      if (!patient) return isDoctor ? "Unknown Doctor" : "Unknown Patient";
      const first = patient.firstName || patient.firstname || "";
      const last = patient.lastName || patient.lastname || "";
      return this.toTitleCase(`${first} ${last}`.trim()) || (isDoctor ? "Unknown Doctor" : "Unknown Patient");
    },

    toTitleCase(value) {
      if (!value) return "";

      return String(value)
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
    },

    convertToLocalTime(date, time) {
      if (!time) return null;

      let hours, minutes;

      // CASE 1: ISO datetime
      if (time.includes("T")) {
        const d = new Date(time);
        if (isNaN(d)) return null;
        hours = d.getUTCHours();
        minutes = d.getUTCMinutes();
      }
      // CASE 2: HH:mm format
      else {
        const parts = time.split(":");
        if (parts.length < 2) return null;
        [hours, minutes] = parts.map(Number);
      }

      // subtrack 6 hours (Server is UTC, Client is -6)
      hours = hours - 6;

      if (hours < 0) {
        hours = hours + 24;
      }

      const localDate = new Date();
      localDate.setHours(hours);
      localDate.setMinutes(minutes);
      localDate.setSeconds(0);

      return localDate;
    },

    formatTime(appointment) {
      if (!appointment) return "";

      const start = this.convertToLocalTime(
        appointment.assignDate,
        appointment.assignedStartTime,
      );

      if (!start) return "";

      return start.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    },

    formatScheduleDate(appointment) {
      const rawDate = appointment?.assignDate || appointment?.assignedDate;
      if (!rawDate) return "";

      const date = this.parseLocalDate(rawDate);
      if (!date) return rawDate;

      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    },

    formatScheduleDoctor(doctor) {
      return this.formatFullName(doctor || null, true).trim() || "Unknown Doctor";
    },

    getDoctorAvatar(doctor) {
      console.log("doc", doctor);
      return doctor?.doctorProfileImage || doctor?.photo || "";
    },

    // Data getters based on timeframe
    getTimeframeData() {
      switch (this.selectedTimeframe) {
        case "daily":
          return {
            utilization: {
              value: "78%",
              trend: 8,
              comparison: { text: "95% vs", subtext: "yesterday" },
            },
            revenue: { value: 28540, trend: 5, comparison: { value: 27120 } },
            appointments: {
              value: 24,
              trend: 12,
              scheduled: 18,
              revenue: 35000,
            },
            confirmed: { value: 18, trend: -5, failedCharges: 3200 },
          };
        case "weekly":
          return {
            utilization: {
              value: "83%",
              trend: 12,
              comparison: { text: "122% vs", subtext: "last week" },
            },
            revenue: { value: 214580, trend: 9, comparison: { value: 181458 } },
            appointments: {
              value: 187,
              trend: 9,
              scheduled: 67,
              revenue: 200000,
            },
            confirmed: { value: 12, trend: -10, failedCharges: 8200 },
          };
        case "monthly":
          return {
            utilization: {
              value: "76%",
              trend: -3,
              comparison: { text: "92% vs", subtext: "last month" },
            },
            revenue: {
              value: 845000,
              trend: 15,
              comparison: { value: 735000 },
            },
            appointments: {
              value: 745,
              trend: 18,
              scheduled: 245,
              revenue: 780000,
            },
            confirmed: { value: 48, trend: 7, failedCharges: 24500 },
          };
        default:
          return {};
      }
    },

    getRevenueData() {
      switch (this.selectedTimeframe) {
        case "daily":
          return {
            labels: ["8AM", "10AM", "12PM", "2PM", "4PM", "6PM"],
            revenue: [12, 18, 25, 22, 28, 15],
            pending: [8, 12, 15, 18, 20, 10],
          };
        case "weekly":
          return {
            labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
            revenue: [45, 60, 55, 85, 100, 70],
            pending: [35, 45, 75, 50, 65, 40],
          };
        case "monthly":
          return {
            labels: ["Week 1", "Week 2", "Week 3", "Week 4"],
            revenue: [180, 210, 195, 240],
            pending: [120, 150, 135, 165],
          };
        default:
          return { labels: [], revenue: [], pending: [] };
      }
    },

    getAllocationData() {
      switch (this.selectedTimeframe) {
        case "daily":
          return {
            labels: ["Collected", "Past Due", "Overdue"],
            values: [70, 18, 12],
          };
        case "weekly":
          return {
            labels: ["Collected", "Past Due", "Overdue"],
            values: [65, 20, 15],
          };
        case "monthly":
          return {
            labels: ["Collected", "Past Due", "Overdue"],
            values: [60, 25, 15],
          };
        default:
          return { labels: [], values: [] };
      }
    },

    getUtilizationStatsData() {
      switch (this.selectedTimeframe) {
        case "daily":
          return {
            total: 2,
            visitsConfirmations: 18,
            visitsTrend: "15%",
            rebooked: 12,
            rebookedTrend: "8%",
            missed: 15,
            missedTrend: "75%",
          };
        case "weekly":
          return {
            total: 2,
            visitsConfirmations: 134,
            visitsTrend: "26%",
            rebooked: 134,
            rebookedTrend: "12%",
            missed: 23,
            missedTrend: "122%",
          };
        case "monthly":
          return {
            total: 2,
            visitsConfirmations: 524,
            visitsTrend: "18%",
            rebooked: 456,
            rebookedTrend: "22%",
            missed: 19,
            missedTrend: "95%",
          };
        default:
          return {};
      }
    },

    getRevenueStatsData() {
      switch (this.selectedTimeframe) {
        case "daily":
          return { revenue: 28.5, pending: 8.2 };
        case "weekly":
          return { revenue: 214, pending: 68 };
        case "monthly":
          return { revenue: 845, pending: 245 };
        default:
          return { revenue: 0, pending: 0 };
      }
    },

    getAllocationStatsData() {
      switch (this.selectedTimeframe) {
        case "daily":
          return {
            total: 2,
            items: [
              { color: "cyan", label: "Collected 18%" },
              { color: "orange", label: "Past Due: 0-30 Days $12,450" },
              { color: "red", label: "Overdue: 31+ Days" },
            ],
          };
        case "weekly":
          return {
            total: 12,
            items: [
              { color: "cyan", label: "Collected 20%" },
              { color: "orange", label: "Past Due: 0-30 Days $86,820" },
              { color: "red", label: "Overdue: 31+ Days" },
            ],
          };
        case "monthly":
          return {
            total: 24,
            items: [
              { color: "cyan", label: "Collected 25%" },
              { color: "orange", label: "Past Due: 0-30 Days $245,000" },
              { color: "red", label: "Overdue: 31+ Days" },
            ],
          };
        default:
          return { total: 0, items: [] };
      }
    },

    getScheduleDataByTimeframe() {
      switch (this.selectedTimeframe) {
        case "daily":
          return [
            {
              patientName: "Sandra Kim",
              time: "9:00 AM",
              date: "Apr 7, 2026",
              doctorName: "Dr. Adams",
            },
            {
              patientName: "James Wilson",
              time: "10:30 AM",
              date: "Apr 7, 2026",
              doctorName: "Dr. Carter",
            },
            {
              patientName: "Emma Davis",
              time: "11:45 AM",
              date: "Apr 7, 2026",
              doctorName: "Dr. Patel",
            },
            {
              patientName: "Robert Chen",
              time: "1:15 PM",
              date: "Apr 7, 2026",
              doctorName: "Dr. Gomez",
            },
            {
              patientName: "Maria Garcia",
              time: "2:30 PM",
              date: "Apr 7, 2026",
              doctorName: "Dr. Khan",
            },
          ];
        case "weekly":
          return [
            {
              patientName: "Sandra Kim",
              time: "9:00 AM",
              date: "Apr 7, 2026",
              doctorName: "Dr. Adams",
            },
            {
              patientName: "Alex Johnson",
              time: "9:30 AM",
              date: "Apr 8, 2026",
              doctorName: "Dr. Carter",
            },
            {
              patientName: "Michael Brown",
              time: "10:00 AM",
              date: "Apr 9, 2026",
              doctorName: "Dr. Patel",
            },
            {
              patientName: "Laura White",
              time: "11:00 AM",
              date: "Apr 10, 2026",
              doctorName: "Dr. Gomez",
            },
            {
              patientName: "Sarah Miller",
              time: "11:30 AM",
              date: "Apr 11, 2026",
              doctorName: "Dr. Khan",
            },
          ];
        case "monthly":
          return [
            {
              patientName: "Multiple Patients",
              time: "Week 1",
              date: "Apr 1, 2026",
              doctorName: "All Doctors",
            },
            {
              patientName: "Multiple Patients",
              time: "Week 2",
              date: "Apr 8, 2026",
              doctorName: "All Doctors",
            },
            {
              patientName: "Multiple Patients",
              time: "Week 3",
              date: "Apr 15, 2026",
              doctorName: "All Doctors",
            },
            {
              patientName: "Multiple Patients",
              time: "Week 4",
              date: "Apr 22, 2026",
              doctorName: "All Doctors",
            },
            {
              patientName: "Multiple Patients",
              time: "Week 5",
              date: "Apr 29, 2026",
              doctorName: "All Doctors",
            },
          ];
        default:
          return [];
      }
    },

    // Helper methods for labels and formatting
    isCustomRangeActive() {
      return this.selectedTimeframe === "custom";
    },

    formatShortRangeDate(value) {
      const date = this.parseLocalDate(value);
      if (!date) return value;

      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    },

    getCustomRangeText() {
      if (!this.isCustomRangeActive()) {
        return "";
      }

      const start = this.formatShortRangeDate(this.customStartDate);
      const end = this.formatShortRangeDate(this.customEndDate);

      return start === end ? start : `${start} - ${end}`;
    },

    getRevenueLabel() {
      if (this.isCustomRangeActive()) {
        return `Revenue (${this.getCustomRangeText()})`;
      }

      switch (this.selectedTimeframe) {
        case "daily":
          return "Today's Revenue";
        case "weekly":
          return "Week-to-Date Revenue";
        case "monthly":
          return "Month-to-Date Revenue";
        default:
          return "Revenue";
      }
    },

    getAppointmentsLabel() {
      if (this.isCustomRangeActive()) {
        return `Upcoming Appointments (${this.getCustomRangeText()})`;
      }

      switch (this.selectedTimeframe) {
        case "daily":
          return "Upcoming Daily Appointments";
        case "weekly":
          return "Upcoming Weekly Appointments";
        case "monthly":
          return "Upcoming Monthly Appointments";
        default:
          return "Upcoming Appointments";
      }
    },

    getScheduleTitle() {
      if (this.isCustomRangeActive()) {
        return `Schedule (${this.getCustomRangeText()})`;
      }

      switch (this.selectedTimeframe) {
        case "daily":
          return "Today's Schedule";
        case "weekly":
          return "This Week's Schedule";
        case "monthly":
          return "This Month's Schedule";
        default:
          return "Schedule";
      }
    },

    getRevenueChartTitle() {
      if (this.isCustomRangeActive()) {
        return `Revenue (${this.getCustomRangeText()})`;
      }

      switch (this.selectedTimeframe) {
        case "daily":
          return "Daily Revenue";
        case "weekly":
          return "Weekly Revenue";
        case "monthly":
          return "Monthly Revenue";
        default:
          return "Revenue";
      }
    },

    getRevenuePeriodChipLabel() {
      if (this.isCustomRangeActive()) {
        return this.getCustomRangeText();
      }

      switch (this.selectedTimeframe) {
        case "daily":
          return "Today";
        case "weekly":
          return "This week";
        case "monthly":
          return "This month";
        default:
          return "Selected period";
      }
    },

    getRevenueChartOptions() {
      return {
        ...this.barOptions,
        scales: {
          ...this.barOptions.scales,
          y: {
            ...this.barOptions.scales.y,
            beginAtZero: true,
            ticks: {
              ...this.barOptions.scales.y.ticks,
              callback: (val) => this.formatCompactCurrency(Number(val)),
            },
            title: {
              display: true,
              text: "Revenue",
              color: "#64748b",
              font: {
                size: 12,
                weight: "600",
              },
            },
          },
          x: {
            ...this.barOptions.scales.x,
            ticks: {
              ...this.barOptions.scales.x.ticks,
              color: "#64748b",
              font: {
                size: 12,
                weight: "600",
              },
            },
          },
        },
      };
    },

    getComparisonPeriod() {
      switch (this.selectedTimeframe) {
        case "daily":
          return "yesterday";
        case "weekly":
          return "last week";
        case "monthly":
          return "last month";
        default:
          return "previous period";
      }
    },

    // Formatting helpers
    formatMetric(value) {
      if (typeof value === "string") return value;
      if (typeof value === "number") {
        if (value > 1000) return this.formatCurrency(value);
        return value.toString();
      }
      return value;
    },

    formatCurrency(value) {
      if (typeof value !== "number") return value;
      if (value >= 1000000) {
        return "$" + (value / 1000000).toFixed(1) + "M";
      }
      if (value >= 1000) {
        return "$" + (value / 1000).toFixed(1) + "K";
      }
      return "$" + value;
    },

    formatCompactCurrency(value) {
      if (typeof value !== "number") return value;

      if (value >= 1000) {
        const num = (value / 1000).toFixed(2);
        return "$" + parseFloat(num) + "K";
      }

      return "$" + value;
    },

    getTrendIcon(direction) {
      if (direction === "up") return "fa-solid fa-caret-up";
      if (direction === "down") return "fa-solid fa-caret-down";
      return "fa-solid fa-minus";
    },
  },
};
</script>

<style scoped>
.dashboard-layout {
  display: flex;
  background-color: #f8fafc;
  min-height: 100vh;
}

.dashboard-main.no-sidebar {
  flex: 1;
  margin-left: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.dashboard-container {
  padding: 40px;
  max-width: 1500px;
  margin: 0 auto;
  width: 100%;
  margin-top: 100px;
  margin-bottom: 100px;
}

.dashboard-content-header {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 18px;
  margin-bottom: 30px;
}

.dashboard-title-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 4px;
}

.title-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-weight: 700;
}

.dashboard-filters-row .title-section {
  flex: 0 0 auto;
}

.dashboard-content-header h1 {
  font-size: clamp(28px, 3vw, 38px);
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  line-height: 1.05;
  letter-spacing: -0.04em;
}

.btn-operations-dashboard {
  color: #007af8;
  padding: 0;
  border-radius: 0;
  font-size: 14px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: color 0.2s ease, transform 0.2s ease;
  text-decoration: none;
  white-space: nowrap;
}

.btn-operations-dashboard:hover {
  color: #0066d1;
  transform: translateX(-1px);
}

.dashboard-filters-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  /* padding: 18px 20px; */
  /* border: 1px solid #e2e8f0; */
  /* border-radius: 20px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.05); */
}

.header-actions {
  display: flex;
  align-items: end;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 15px;
  flex: 1 1 auto;
}

.timeframe-dropdown {
  position: relative;
  display: flex;
  align-items: center;
}

.timeframe-dropdown select {
  appearance: none;
  background: white;
  border: 1px solid #e2e8f0;
  color: #1e293b;
  padding: 10px 35px 10px 18px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  outline: none;
  transition: all 0.2s;
  min-width: 170px;
  min-height: 46px;
}

.timeframe-dropdown select:hover {
  border-color: #007af8;
}

.timeframe-dropdown .dropdown-icon {
  position: absolute;
  right: 12px;
  color: #94a3b8;
  pointer-events: none;
  font-size: 12px;
}

.date-range-picker {
  display: flex;
  align-items: flex-end;
  gap: 12px;
}

.date-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.date-field label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
}

.date-field input {
  min-height: 46px;
  padding: 8px 12px;
  border: 1px solid #dbe4ee;
  border-radius: 10px;
  background: #fff;
  color: #1e293b;
  font-size: 14px;
  font-weight: 600;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.date-field input:focus,
.date-field input:hover {
  border-color: #007af8;
  box-shadow: 0 0 0 3px rgba(0, 122, 248, 0.12);
}

.date-range-separator {
  padding-bottom: 10px;
  color: #64748b;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.btn-generate {
  background: white;
  border: 1px solid #e2e8f0;
  color: #007af8;
  padding: 11px 18px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  text-decoration: none;
}

.btn-generate:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.btn-add-appt {
  background: #007af8;
  border: none;
  color: white;
  padding: 10px 18px;
  border-radius: 8px;
  font-weight: 500;
  font-size: 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  text-decoration: none;
}

.btn-add-appt:hover {
  background: #0066d1;
}

.user-avatar img {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  object-fit: cover;
}

.metrics-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-bottom: 40px;
}

.metric-card {
  background: white;
  padding: 28px;
  border-radius: 18px;
  display: flex;
  flex-direction: column;
  min-height: 160px;
  height: 100%;
}

.metric-chart-icon {
  color: #007af8;
  font-size: 16px;
  opacity: 0.6;
}

.metric-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.metric-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.metric-label {
  color: #424852;
  font-size: 20px;
  font-weight: 500;
}

.metric-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  justify-content: center;
}

.main-val {
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
}

.main-val .number {
  font-size: 34px;
  font-weight: 500;
  color: #1e293b;
  line-height: 1.2;
}

.utilization-rate-group {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.metric-value-group {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.metric-side-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  /* gap: 6px; */
}

.metric-side-label {
  font-size: 10px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.utilization-trend {
  margin: 0;
}

.trend {
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 3px 8px;
  border-radius: 20px;
  background: #f8fafc;
}

.trend.up {
  color: #22c55e;
}

.trend.down {
  color: #ef4444;
}

.trend.danger {
  color: #ff7043;
}

.sub-val {
  font-size: 14px;
  color: #94a3b8;
  font-weight: 400;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.vs-text {
  color: #22c55e;
  font-weight: 500;
}

.vs-text-sub {
  color: #5a626d;
  font-weight: 400;
}

.mini-trend {
  font-weight: 500;
}

.mini-trend.up {
  color: #22c55e;
}

.mini-trend.down {
  color: #ef4444;
}

.separator {
  color: #94a3b8;
}

/* Skeleton Loader Styles */
.skeleton-animated {
  background: linear-gradient(90deg, #f1f5f9 25%, #f8fafc 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 8px;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

.skeleton-card {
  pointer-events: none;
  border: 1px solid #f1f5f9;
}

.skeleton-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.skeleton-label {
  width: 140px;
  height: 20px;
}

.skeleton-label-lg {
  width: 200px;
  height: 24px;
}

.skeleton-icon {
  width: 20px;
  height: 20px;
  border-radius: 4px;
}

.skeleton-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skeleton-main {
  width: 80px;
  height: 38px;
}

.skeleton-sub {
  width: 180px;
  height: 16px;
}

.skeleton-body-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-top: 10px;
}

.skeleton-graph-placeholder {
  width: 100%;
  height: 240px;
  border-radius: 12px;
}

.skeleton-text-row {
  width: 70%;
  height: 18px;
}

.metric-card.skeleton-card {
  min-height: 140px;
}

.chart-card.full-height {
  height: 500px;
}

.overview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.section-heading {
  font-size: 28px;
  font-weight: 500;
  color: #1e293b;
  margin: 0;
}

.overview-actions {
  display: flex;
  align-items: center;
  gap: 15px;
}

.search-dropdown {
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 14px;
  gap: 10px;
  color: #94a3b8;
}

.search-dropdown select {
  border: none;
  background: none;
  font-size: 15px;
  color: #1e293b;
  font-weight: 500;
  outline: none;
  cursor: pointer;
}

.btn-add-primary {
  background: #007af8;
  border: none;
  color: white;
  padding: 10px 22px;
  border-radius: 8px;
  font-weight: 500;
  font-size: 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
}

.dashboard-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  align-items: stretch;
}

.dashboard-col {
  display: flex;
  flex-direction: column;
  gap: 30px;
  height: 100%;
}

.chart-card,
.schedule-card {
  background: white;
  border-radius: 20px;
  padding: 28px;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 500px;
  max-height: 620px;
  overflow: visible;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.card-header h3 {
  font-size: 22px;
  font-weight: 500;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
}

.card-header-icon {
  color: #007af8;
  font-size: 20px;
}

.card-header-info {
  color: #94a3b8;
  cursor: help;
  font-size: 18px;
}

.count-badge {
  background: #f1f5f9;
  color: #64748b;
  font-size: 13px;
  padding: 3px 8px;
  border-radius: 6px;
  margin-left: 6px;
  font-weight: 400;
}

.card-controls {
  display: flex;
  gap: 18px;
  color: #64748b;
  font-size: 18px;
}

.card-controls i {
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
}

.card-controls i:hover {
  background: #f1f5f9;
  color: #007af8;
}

.chart-box {
  height: 260px;
  flex-shrink: 0;
}

.chart-bottom-stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  padding-top: 28px;
  border-top: 1px solid #f1f5f9;
  margin-top: 24px;
  gap: 10px;
}

.stat-col {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.stat-main {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.stat-val {
  font-size: 28px;
  font-weight: 500;
  color: #1e293b;
}

.stat-label {
  font-size: 16px;
  color: #6d7785;
  font-weight: 400;
}

.stat-footer-alt {
  display: flex;
  align-items: center;
  gap: 10px;
}

.trend.success {
  color: #22c55e;
  font-weight: 500;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 3px;
}

.weekday-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  color: #94a3b8;
  font-size: 14px;
  font-weight: 400;
  padding: 0 10px;
}

.schedule-filter-v2 {
  color: #007af8;
  font-weight: 500;
  font-size: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 6px 12px;
  background: #f1f5f9;
  border-radius: 8px;
  text-decoration: none;
}

.schedule-table {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.schedule-head-v3,
.schedule-row-v3 {
  display: grid;
  grid-template-columns: 1.3fr 0.9fr 1fr 1.1fr;
  gap: 16px;
  align-items: center;
}

.schedule-head-v3 {
  padding: 0 0 12px;
  border-bottom: 1px solid #e5edf5;
  color: #7c8a9a;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.schedule-row-v3 {
  padding: 18px 0;
  border-bottom: 1px solid #f8fafc;
}

.schedule-cell {
  font-size: 15px;
  font-weight: 500;
  color: #526072;
}

.patient-cell {
  color: #1e293b;
  font-weight: 600;
}

.revenue-summary {
  margin-top: 22px;
}

.revenue-summary-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px 20px;
  border-radius: 20px;
  background: linear-gradient(135deg, #f6fdff 0%, #eef7ff 100%);
  border: 1px solid #d8eef7;
  box-shadow: 0 14px 30px rgba(77, 208, 225, 0.12);
}

.revenue-summary-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.revenue-label-group {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.revenue-label-dot {
  width: 12px;
  height: 12px;
  border-radius: 999px;
  background: linear-gradient(135deg, #4dd0e1 0%, #0891b2 100%);
  box-shadow: 0 0 0 4px rgba(77, 208, 225, 0.16);
}

.revenue-label-text {
  color: #0f172a;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.revenue-period-chip {
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(8, 145, 178, 0.18);
  color: #0f766e;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.revenue-summary-value {
  color: #0f172a;
  font-size: clamp(28px, 3vw, 38px);
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.03em;
}

.revenue-summary-note {
  color: #526072;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.5;
}

.payments-container.v2 {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
  min-height: 220px;
}

.allocation-summary {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 8px;
}

.appointments-summary-chip {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  align-self: flex-start;
  padding: 10px 14px;
  border-radius: 999px;
  background: linear-gradient(135deg, #f8fafc 0%, #eef6ff 100%);
  border: 1px solid #dbeafe;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
}

.allocated-doctors-summary {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 20px;
  border-radius: 20px;
  background: linear-gradient(135deg, #ffffff 0%, #eff6ff 100%);
  border: 1px solid #dbeafe;
  box-shadow: 0 16px 30px rgba(0, 122, 248, 0.12);
}

.appointments-summary-chip-label {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
}

.appointments-summary-chip-value {
  font-size: 22px;
  font-weight: 700;
  color: #475569;
  line-height: 1;
}

.allocated-doctors-icon {
  width: 58px;
  height: 58px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #007af8 0%, #00b8f5 100%);
  color: white;
  font-size: 24px;
  box-shadow: 0 14px 28px rgba(0, 122, 248, 0.28);
  flex-shrink: 0;
}

.allocated-doctors-content {
  display: flex;
  gap: 14px;
  min-width: 0;
  align-items: center;
}

.allocated-doctors-count {
  font-size: 52px;
  font-weight: 700;
  color: #1e293b;
  letter-spacing: -1.8px;
  line-height: 1;
}

.allocated-doctors-label {
  font-size: 17px;
  font-weight: 600;
  color: #4f5f75;
  letter-spacing: 0.01em;
  line-height: 1.2;
}

.payment-stats-list-v2 {
  list-style: none;
  padding: 0;
  margin: 14px 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 240px;
  overflow-y: auto;
}

.payment-stats-list-v2 li {
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 15px;
  color: #616a75;
  font-weight: 400;
}

.others-row {
  margin-top: 4px;
}

.others-badge {
  display: inline-flex;
  align-items: center;
  background: #f1f5f9;
  color: #64748b;
  font-size: 13px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 20px;
  gap: 4px;
}

.dot-v2 {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.dot-v2.cyan {
  background: #00d2eb;
}

.dot-v2.orange {
  background: #ffb74d;
}

.dot-v2.blue {
  background: #007af8;
}

.dot-v2.red {
  background: #ff7043;
}

.dot-v2.purple {
  background: #6f42c1;
}

.dot-v2.lightCyan {
  background: #4dd0e1;
}

.dot-v2.lightBlue {
  background: #42a5f5;
}

.dot-v2.lavender {
  background: #ba68c8;
}

.total-summary-label {
  font-size: 16px;
  font-weight: 500;
  color: #1e293b;
  margin-top: 15px;
}

.donut-chart-wrapper {
  position: relative;
  width: 300px;
  height: 300px;
}

.donut-center-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  display: flex;
  flex-direction: column;
}

.center-label {
  font-size: 14px;
  color: #94a3b8;
  font-weight: 400;
}

.center-val {
  font-size: 26px;
  font-weight: 500;
  color: #1e293b;
}

.card-footer-v2 {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 28px;
  padding-top: 24px;
  border-top: 1px solid #f1f5f9;
}

.btn-link-v2 {
  background: none;
  border: none;
  color: #007af8;
  font-weight: 500;
  font-size: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.btn-primary-blue {
  background: #007af8;
  color: white;
  border: none;
  padding: 12px 28px;
  border-radius: 8px;
  font-weight: 500;
  font-size: 15px;
  cursor: pointer;
}

.shadow-sm {
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
}

.footer-container {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background-color: white;
  padding: 20px 20px 40px;
  border-top: 1px solid #f1f5f9;
  box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.05);
}

.page-header-wrapper {
  position: relative;
  z-index: 1;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
}

.modal-container {
  background: white;
  border-radius: 20px;
  width: 90%;
  max-width: 1200px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    transform: translateY(-30px);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px;
  border-bottom: 1px solid #f1f5f9;
}

.modal-header h2 {
  font-size: 24px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 20px;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s;
}

.modal-close:hover {
  background: #f1f5f9;
  color: #1e293b;
}

.modal-body {
  padding: 32px;
  overflow-y: auto;
  flex: 1;
}

.modal-summary-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 30px;
}

.summary-card {
  background: #f8fafc;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  border: 1px solid #e2e8f0;
}

.summary-icon {
  width: 48px;
  height: 48px;
  background: #e0f2fe;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #007af8;
  font-size: 20px;
}

.summary-content {
  display: flex;
  flex-direction: column;
}

.summary-label {
  font-size: 14px;
  color: #64748b;
  margin-bottom: 4px;
}

.summary-value {
  font-size: 24px;
  font-weight: 600;
  color: #1e293b;
}

.modal-filter {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.modal-filter label {
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
}

.modal-filter select {
  padding: 8px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  color: #1e293b;
  background: white;
  cursor: pointer;
  outline: none;
  min-width: 120px;
}

.modal-filter select:hover {
  border-color: #007af8;
}

.modal-table-container {
  overflow-x: auto;
  margin-bottom: 30px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}

.modal-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;
}

.modal-table th {
  background: #f8fafc;
  padding: 16px 20px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 2px solid #e2e8f0;
}

.modal-table td {
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  color: #1e293b;
  font-size: 14px;
}

.modal-table tr:hover {
  background: #f8fafc;
}

.doctor-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.doctor-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
}

.progress-bar-container {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 100px;
}

.progress-bar {
  height: 6px;
  background: #007af8;
  border-radius: 3px;
  transition: width 0.3s ease;
}

.progress-bar-container span {
  font-size: 12px;
  color: #64748b;
  min-width: 35px;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.active {
  background: #dcfce7;
  color: #166534;
}

.status-badge.onleave {
  background: #fff3cd;
  color: #856404;
}

.status-badge.inactive {
  background: #fee2e2;
  color: #991b1b;
}

.collections-breakdown {
  background: #f8fafc;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #e2e8f0;
}

.collections-breakdown h3 {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 20px 0;
}

.breakdown-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.breakdown-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.breakdown-label {
  font-size: 14px;
  color: #64748b;
}

.breakdown-value {
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
}

.breakdown-percent {
  font-size: 14px;
  color: #64748b;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px 32px;
  border-top: 1px solid #f1f5f9;
  background: #f8fafc;
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
}

.btn-secondary {
  background: white;
  border: 1px solid #e2e8f0;
  color: #64748b;
  padding: 10px 24px;
  border-radius: 8px;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.btn-primary {
  background: #007af8;
  border: none;
  color: white;
  padding: 10px 24px;
  border-radius: 8px;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary:hover {
  background: #0066d1;
}

@media (max-width: 1280px) {
  .metrics-row {
    grid-template-columns: repeat(2, 1fr);
  }

  .dashboard-grid {
    grid-template-columns: 1fr;
  }

  .chart-card,
  .schedule-card {
    min-height: auto;
    max-height: none;
  }

  .payments-container.v2 {
    gap: 24px;
  }

  .modal-summary-cards {
    grid-template-columns: 1fr;
  }

  .breakdown-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 1024px) {
  .dashboard-container {
    padding: 28px 24px 120px;
    margin-top: 90px;
  }

  .dashboard-content-header,
  .overview-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .header-actions,
  .overview-actions {
    width: 100%;
    flex-wrap: wrap;
  }

  .dashboard-filters-row {
    padding: 16px;
  }

  .date-range-picker {
    width: 100%;
    justify-content: flex-start;
    flex-wrap: wrap;
  }

  .timeframe-dropdown select,
  .date-field input,
  .btn-generate,
  .btn-add-appt {
    min-height: 44px;
  }

  .chart-card,
  .schedule-card,
  .metric-card {
    padding: 24px;
  }

  .card-header {
    align-items: flex-start;
    gap: 14px;
  }

  .card-header h3 {
    flex-wrap: wrap;
    line-height: 1.3;
  }

  .chart-box {
    height: 220px;
  }

  .chart-bottom-stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .revenue-summary-card {
    padding: 16px 18px;
  }

  .revenue-summary-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
  }

  .payments-container.v2 {
    flex-direction: column;
    align-items: stretch;
  }

  .payments-info {
    width: 100%;
  }

  .donut-chart-wrapper {
    width: min(100%, 280px);
    height: min(100vw - 96px, 280px);
    margin: 0 auto;
  }

  .footer-container {
    position: static;
    padding: 16px 20px 24px;
    box-shadow: none;
  }
}

@media (max-width: 768px) {
  .metrics-row {
    grid-template-columns: 1fr;
    gap: 18px;
  }

  .dashboard-container {
    padding: 20px 16px 96px;
    margin-top: 80px;
    margin-bottom: 40px;
  }

  .dashboard-content-header h1 {
    font-size: 28px;
  }

  .dashboard-title-row {
    align-items: center;
    gap: 10px;
    min-height: auto;
  }

  .section-heading {
    font-size: 24px;
  }

  .header-actions {
    align-items: stretch;
    gap: 12px;
  }

  .dashboard-filters-row {
    padding: 14px;
    border-radius: 18px;
  }

  .timeframe-dropdown,
  .date-range-picker,
  .timeframe-dropdown select,
  .btn-generate,
  .btn-add-appt {
    width: 100%;
  }

  .btn-operations-dashboard {
    position: static;
    transform: none;
    font-size: 13px;
  }

  .date-range-picker {
    align-items: stretch;
    padding: 14px;
  }

  .date-field {
    width: 100%;
  }

  .date-range-separator {
    padding-bottom: 0;
  }

  .btn-generate,
  .btn-add-appt {
    justify-content: center;
  }

  .metric-card,
  .chart-card,
  .schedule-card {
    padding: 20px 18px;
    border-radius: 16px;
  }

  .metric-label {
    font-size: 18px;
  }

  .main-val .number {
    font-size: 30px;
  }

  .sub-val {
    gap: 6px;
    font-size: 13px;
  }

  .dashboard-col {
    gap: 20px;
  }

  .payments-container.v2 {
    flex-direction: column;
    gap: 20px;
  }

  .allocated-doctors-summary {
    width: 100%;
    padding: 16px;
  }

  .allocated-doctors-count {
    font-size: 42px;
  }

  .allocated-doctors-label {
    font-size: 15px;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 18px;
  }

  .card-controls {
    width: 100%;
    justify-content: flex-start;
    gap: 12px;
  }

  .chart-box {
    height: 200px;
  }

  .chart-bottom-stats-grid {
    grid-template-columns: 1fr 1fr;
    padding-top: 20px;
    margin-top: 20px;
  }

  .stat-val {
    font-size: 24px;
  }

  .stat-label {
    font-size: 14px;
  }

  .schedule-row-v3 {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    padding: 16px 0;
  }

  .schedule-head-v3 {
    display: none;
  }

  .schedule-cell {
    font-size: 14px;
  }

  .patient-cell {
    grid-column: 1 / -1;
    font-size: 16px;
  }

  .payment-stats-list-v2 {
    max-height: none;
  }

  .payment-stats-list-v2 li {
    align-items: flex-start;
    gap: 10px;
  }

  .donut-chart-wrapper {
    width: min(100%, 240px);
    height: min(70vw, 240px);
  }

  .card-footer-v2 {
    margin-top: 20px;
    padding-top: 18px;
  }

  .btn-primary-blue {
    width: 100%;
    justify-content: center;
  }

  .modal-container {
    width: calc(100% - 20px);
    max-height: calc(100vh - 20px);
    border-radius: 16px;
  }

  .modal-body {
    padding: 18px;
  }

  .modal-header {
    padding: 18px;
    align-items: flex-start;
    gap: 12px;
  }

  .modal-header h2 {
    font-size: 20px;
  }

  .modal-footer {
    padding: 18px;
    flex-direction: column;
  }

  .modal-filter {
    flex-direction: column;
    align-items: stretch;
  }

  .modal-filter select {
    width: 100%;
  }

  .modal-table {
    min-width: 640px;
  }

  .summary-card {
    padding: 16px;
  }

  .summary-value {
    font-size: 22px;
  }

  .footer-container {
    padding: 14px 16px 20px;
  }
}

@media (max-width: 480px) {
  .dashboard-container {
    padding: 18px 12px 88px;
  }

  .dashboard-content-header h1 {
    font-size: 24px;
  }

  .section-heading {
    font-size: 22px;
  }

  .metric-card,
  .chart-card,
  .schedule-card {
    padding: 16px 14px;
  }

  .metric-label,
  .card-header h3 {
    font-size: 17px;
  }

  .main-val .number {
    font-size: 26px;
  }

  .trend {
    font-size: 12px;
    padding: 3px 6px;
  }

  .chart-box {
    height: 180px;
  }

  .chart-bottom-stats-grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .appointments-summary-chip {
    width: 100%;
    justify-content: space-between;
  }

  .allocated-doctors-summary {
    padding: 14px;
    gap: 12px;
  }

  .allocated-doctors-icon {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    font-size: 20px;
  }

  .allocated-doctors-count {
    font-size: 34px;
  }

  .allocated-doctors-label {
    font-size: 14px;
  }

  .payment-stats-list-v2 li {
    font-size: 14px;
  }

  .revenue-summary-value {
    font-size: 28px;
  }

  .schedule-filter-v2 {
    width: 100%;
    justify-content: center;
  }

  .schedule-row-v3 {
    grid-template-columns: 1fr;
  }

  .schedule-cell {
    font-size: 13px;
  }

  .modal-table {
    min-width: 560px;
  }
}

/* Tooltip Styles */
.info-tooltip-container {
  position: relative;
  display: flex;
  align-items: center;
}

.tooltip-content {
  visibility: hidden;
  width: 280px;
  background-color: #1e293b;
  color: #fff;
  text-align: left;
  border-radius: 12px;
  padding: 16px;
  position: absolute;
  z-index: 1001;
  bottom: 150%;
  right: -10px;
  opacity: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 14px;
  line-height: 1.6;
  pointer-events: none;
  transform: translateY(10px) scale(0.95);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.tooltip-content::after {
  content: "";
  position: absolute;
  top: 100%;
  right: 15px;
  border-width: 8px;
  border-style: solid;
  border-color: #1e293b transparent transparent transparent;
}

.info-tooltip-container:hover .tooltip-content {
  visibility: visible;
  opacity: 1;
  transform: translateY(0) scale(1);
}

.tooltip-content p {
  margin: 0 0 8px 0;
}

.tooltip-content p:last-child {
  margin-bottom: 0;
}

.tooltip-content strong {
  color: #4dd0e1;
  font-weight: 600;
}

/* Metric Eye Button */
.metric-header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.metric-eye-btn {
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 16px;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 6px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.metric-eye-btn:hover {
  background: #eff6ff;
  color: #007af8;
}

/* Utilization Modal Styles */
.utilization-modal {
  max-width: 1060px;
}

.modal-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.modal-header-icon {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #007af8 0%, #38bdf8 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  flex-shrink: 0;
}

.modal-subtitle {
  margin: 2px 0 0;
  font-size: 13px;
  color: #94a3b8;
  font-weight: 400;
}

/* KPI Grid */
.util-kpi-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  margin-bottom: 24px;
}

.util-kpi-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 14px;
  transition: box-shadow 0.2s;
}

.util-kpi-card:hover {
  box-shadow: 0 4px 12px rgba(0, 122, 248, 0.08);
}

.util-kpi-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
}

.util-kpi-icon.blue {
  background: #dbeafe;
  color: #007af8;
}

.util-kpi-icon.green {
  background: #dcfce7;
  color: #16a34a;
}

.util-kpi-icon.orange {
  background: #ffedd5;
  color: #ea580c;
}

.util-kpi-icon.red {
  background: #fee2e2;
  color: #dc2626;
}

.util-kpi-icon.purple {
  background: #ede9fe;
  color: #7c3aed;
}

.util-kpi-icon.teal {
  background: #ccfbf1;
  color: #0d9488;
}

.util-kpi-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.util-kpi-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.util-kpi-value {
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
}

/* Note */
.util-note {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  padding: 12px 16px;
  font-size: 13px;
  color: #1d4ed8;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 24px;
  line-height: 1.5;
}

.util-note i {
  flex-shrink: 0;
  margin-top: 1px;
}

/* Section */
.util-section {
  margin-bottom: 28px;
}

.util-section-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.util-section-title i {
  color: #007af8;
}

/* Doctor overbooking horizontal bar chart */
.util-doctor-chart {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.util-doctor-row {
  display: grid;
  grid-template-columns: 180px 1fr auto;
  align-items: center;
  gap: 14px;
}

.util-doctor-name {
  font-size: 14px;
  font-weight: 500;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.util-bar-track {
  background: #f1f5f9;
  height: 10px;
  border-radius: 999px;
  overflow: hidden;
}

.util-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #f06722d3, #f06c25b7);
  border-radius: 999px;
  transition: width 0.5s ease;
  min-width: 4px;
}

.util-doctor-stats {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

/* Badges */
.util-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.util-badge.overbooked {
  background: #fee2e2;
  color: #dc2626;
}

/* Skeleton Loading */
.skeleton-animated {
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: skeletonShimmer 1.5s infinite linear;
}

@keyframes skeletonShimmer {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

.skeleton-label-lg {
  height: 24px;
  width: 200px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.util-skeleton-body {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.util-skeleton-chart,
.util-skeleton-table {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.skeleton-bar-row {
  height: 48px;
  border-radius: 12px;
  width: 100%;
}

.skeleton-table-row {
  height: 52px;
  border-radius: 8px;
  width: 100%;
}

.util-kpi-card.skeleton-animated {
  height: 86px;
  border-radius: 16px;
  border: none;
}

.util-badge.excess {
  background: #ffedd5;
  color: #ea580c;
}

/* Slot table */
.util-slot-table {
  min-width: 640px;
}

.util-time-chip {
  display: inline-flex;
  align-items: center;
  background: #f1f5f9;
  color: #475569;
  font-size: 12px;
  font-weight: 500;
  padding: 3px 9px;
  border-radius: 6px;
  white-space: nowrap;
}

/* Empty state */
.util-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 24px;
  color: #94a3b8;
  text-align: center;
}

.util-empty i {
  font-size: 40px;
  color: #22c55e;
}

.util-empty p {
  font-size: 15px;
  margin: 0;
}

.revenue-disclaimer {
  font-size: 13px;
  color: gray;
}
</style>
